/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Modal,
  Image,
  Switch,
  Animated,
  Keyboard,
} from "react-native";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createDocument,
  updateDocument,
} from "../db_calls";
import { Timestamp } from "firebase/firestore";
import { useBookings, useProducts, useSettings, useAuthStore, useNewBookingStore, useStripeTestMode } from "../stores";
import { centsToDollars, dollarsToCents, formatCentsAsUSD, formatDollarsAsUSD, formatDollarsAsUSDWithCents } from "../utils/formatting";
import { calculateDurationDiscountUtil } from "../utils/discountCalculations";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, auth, storage } from "../firebase.config";
import { db } from "../firebase.config";
import { getCollectionPath, getSettingsPath } from "../utils/dbPaths";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { doc, getDoc, collection, getDocs, setDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import AuthHeader from "../components/AuthHeader";
import GoogleAddressInput from "../components/GoogleAddressInput";
import {
  formatPhoneNumberProgressive as formatPhoneNumber,
  getToday,
  getTomorrow,
  capitalizeWords,
} from "../utils/formatting";
import { loadWaiverTemplate, validateStoredWaiver } from "../utils/waiverCheck";

// Helper function to capitalize all words
const capitalizeAllWords = (text) => {
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to capitalize first word only
const capitalizeFirstWord = (text) => {
  if (!text) return text;
  const words = text.split(' ');
  if (words.length === 0) return text;
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
  return words.join(' ');
};
const functions = getFunctions(app);

// Default values constants
const DEFAULT_FIRST_NAME = "Ricky";
const DEFAULT_LAST_NAME = "Bobby";
const DEFAULT_CUSTOMER_EMAIL = "hieb.fritz@gmail.com";
const DEFAULT_CUSTOMER_PHONE = formatPhoneNumber("2393369177");
const DEFAULT_CUSTOMER_ADDRESS = "11203 Butler Drive, Bonita Springs, FL, USA";
const DEFAULT_START_DATE = getToday();
const DEFAULT_END_DATE = getTomorrow();
const DEFAULT_CURRENT_MONTH = new Date();
const DEFAULT_DELIVERY_NEEDED = false;
const DEFAULT_HOME_PICKUP_NEEDED = false;
const DEFAULT_IN_STORE_PICKUP = true;

// Short customer-facing booking ID: last 5 digits of the long (database) booking id
const shortBookingIdFromLong = (longBookingId) => {
  if (!longBookingId) return "";
  const digits = longBookingId.replace(/\D/g, "");
  if (digits.length >= 5) return digits.slice(-5);
  return digits || longBookingId.slice(-5);
};

// Wraps "amount available" text with green style and a one-time 0.5s blink via CSS animation.
// Keyframes are in index.html; inline style ensures animation reaches the DOM (StyleSheet may strip it).
const AvailabilityText = ({ style, children }) => (
  <View style={{ animation: "availability-blink 0.5s ease-in-out 1" }}>
    <Text style={style}>{children}</Text>
  </View>
);

const RentalBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Subscribe to Zustand stores for live updates
  const bookingsFromStore = useBookings(state => state.bookings);
  const products1FromStore = useProducts(state => state.products1);
  const products2FromStore = useProducts(state => state.products2);
  const settings = useSettings(state => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);

  // Inject CSS animation for loading bar and availability blink
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes loading-pulse {
        0% {
          transform: translateX(-100%);
        }
        50% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(100%);
        }
      }
      @keyframes availability-blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }
      .availability-blink-once {
        animation: availability-blink 0.5s ease-in-out 1;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // EDIT MODE STATE
  const [isEditMode, setIsEditMode] = useState(false); // TODO: Set from location.state or route
  const [originalBookingId, setOriginalBookingId] = useState(null);
  const [originalBookingData, setOriginalBookingData] = useState(null);
  const [originalBicycleQuantities, setOriginalBicycleQuantities] = useState(
    {}
  ); // Track original bike quantities
  const [adminNotes, setAdminNotes] = useState(""); // Admin notes stored in master booking
  const [adminNotesSaving, setAdminNotesSaving] = useState(false);
  const [showAdminNotes, setShowAdminNotes] = useState(() => {
    // Default to showing if admin notes exist (will be updated when booking loads)
    return false;
  }); // Show/hide admin notes
  const adminNotesTimerRef = React.useRef(null);

  // Data from stores (Direct access, no local state syncing)
  const inventory = products1FromStore || [];
  const addOns = products2FromStore || [];
  const bookings = bookingsFromStore || [];

  // Derived state from settings store (Replacing local state syncing)
  const salesTaxPercent = settings?.salesTax?.percent ?? null;
  const bookingPlatformFee = settings?.bookingPlatform?.perBookingFee ?? 0;
  const cardProcessingFeePercent = settings?.cardProcessingFee?.percent ?? 3;
  const cardProcessingFeeForPurchases = settings?.cardProcessingFee?.forPurchases ?? false;
  const startingAddress = settings?.startingAddress?.address ?? "";
  const mileageRate = settings?.mileageRate?.rate ?? 0;
  const loadingFee = settings?.loadingFee?.fee ?? 0;
  const deliveryInfo = settings?.deliveryInfo?.info ?? "";
  const pickupInfo = settings?.pickupInfo?.info ?? "";
  const useDeliveryDiscounts = settings?.useDeliveryDiscounts?.enabled ?? true;
  const deliveryDiscounts = settings?.deliveryDiscounts?.discounts ?? [];
  const useDurationDiscounts = settings?.useDurationDiscounts?.enabled ?? false;
  const durationDiscounts = settings?.durationDiscounts?.discounts ?? [];
  const appHeaderTitle = settings?.appHeaderTitle?.title ?? "Product Rentals";
  const storeName = settings?.storeName?.name ?? "";
  const storeLogo = settings?.storeLogo?.url ?? "";
  const cancellationPolicy = settings?.cancellationPolicy?.policy ?? "Our cancellation policy required 48 hours advance notice for a full refund, and a 24 hour advance notice for a 75% refund. All other cancellations only receive 50% refund.";
  const shopInfo = {
    address: startingAddress,
    phone: settings?.storePhone?.phone ?? "",
    email: settings?.storeEmail?.email ?? ""
  };
  const product1Title = settings?.product1Title?.title ?? "Bicycles";
  const product2Title = settings?.product2Title?.title ?? "Add-Ons";

  // UI state
  const loading = !settings || Object.keys(settings).length === 0; // Simple loading check
  // Removed individual loaded flags and loadingProgress
  const [manualBookingProcessing, setManualBookingProcessing] = useState(false);
  const [manualBookingMode, setManualBookingMode] = useState("signPay");
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // Removed local state definitions for settings (salesTaxPercent etc)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  // Get booking data from Zustand store
  const {
    startDate: storeStartDate,
    endDate: storeEndDate,
    currentMonth: storeCurrentMonth,
    selectedBikes: storeSelectedBikes,
    dailyRateOverrideCents,
    selectedAddOns: storeSelectedAddOns,
    deliveryNeeded: storeDeliveryNeeded,
    homePickupNeeded: storeHomePickupNeeded,
    inStorePickup: storeInStorePickup,
    gatedCommunity: storeGatedCommunity,
    communityName: storeCommunityName,
    addressNotes: storeAddressNotes,
    firstName: storeFirstName,
    lastName: storeLastName,
    customerEmail: storeCustomerEmail,
    customerPhone: storeCustomerPhone,
    addressInput: storeAddressInput,
    addressConfirmed: storeAddressConfirmed,
    waiverSigned: storeWaiverSigned,
    waiverData: storeWaiverData,
    setStartDate,
    setEndDate,
    setCurrentMonth,
    setSelectedBikes,
    updateBookingData,
    setSelectedAddOns,
    setDeliveryNeeded,
    setHomePickupNeeded,
    setInStorePickup,
    setGatedCommunity,
    setCommunityName,
    setAddressNotes,
    setFirstName,
    setLastName,
    setCustomerEmail,
    setCustomerPhone,
    setAddressInput,
    setAddressConfirmed,
    setWaiverSigned,
    setWaiverData,
    clearBooking,
  } = useNewBookingStore();

  // Local state for UI (not persisted)
  const [submitting, setSubmitting] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showDeliveryInfoFull, setShowDeliveryInfoFull] = useState(false);
  const [showPickupInfoFull, setShowPickupInfoFull] = useState(false);
  const [showAddressValidationModal, setShowAddressValidationModal] = useState(false);
  const [addressDistanceMiles, setAddressDistanceMiles] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [showAddressWrongModal, setShowAddressWrongModal] = useState(false);
  const [showDurationDiscountsInCalendar, setShowDurationDiscountsInCalendar] = useState(true);
  const [showCheckingWaiverModal, setShowCheckingWaiverModal] = useState(false);

  // Clear stale booking data on page load (last save older than 5 minutes)
  const NEW_BOOKING_STALE_MS = 300000;
  useEffect(() => {
    const state = useNewBookingStore.getState();
    const lastUpdatedAt = state.lastUpdatedAt;
    const now = Date.now();
    const isStale =
      lastUpdatedAt == null || typeof lastUpdatedAt !== "number" || now - lastUpdatedAt > NEW_BOOKING_STALE_MS;
    if (isStale) {
      state.clearBooking();
    }
  }, []);

  // Phone number input refs (10 boxes)
  const phoneInputRefs = React.useRef([]);

  // Use store values (with defaults for edit mode or initial load)
  // In edit mode, use originalBookingData; otherwise use store values
  // Important: Allow null values to properly clear dates
  const startDate = isEditMode 
    ? (originalBookingData?.startDate?.toDate ? originalBookingData.startDate.toDate() : (originalBookingData?.startDate ? new Date(originalBookingData.startDate) : null))
    : (storeStartDate ? new Date(storeStartDate) : null);
  const endDate = isEditMode 
    ? (originalBookingData?.endDate?.toDate ? originalBookingData.endDate.toDate() : (originalBookingData?.endDate ? new Date(originalBookingData.endDate) : null))
    : (storeEndDate ? new Date(storeEndDate) : null);
  const currentMonth = storeCurrentMonth ? new Date(storeCurrentMonth) : DEFAULT_CURRENT_MONTH;
  const selectedBikes = isEditMode ? (() => {
    const bikes = {};
    if (originalBookingData?.product1) {
      originalBookingData.product1.forEach(item => {
        bikes[item.name] = item.quantity || 0;
      });
    }
    return bikes;
  })() : (storeSelectedBikes || {});
  const selectedAddOns = isEditMode ? (() => {
    const addOns = {};
    if (originalBookingData?.product2) {
      originalBookingData.product2.forEach(item => {
        if (item.option === "per-bike") {
          addOns[item.id] = true;
        } else {
          addOns[item.id] = { quantity: item.quantity || 0 };
        }
      });
    }
    return addOns;
  })() : (storeSelectedAddOns || {});
  const deliveryNeeded = isEditMode ? (originalBookingData?.delivery || false) : (storeDeliveryNeeded ?? DEFAULT_DELIVERY_NEEDED);
  const homePickupNeeded = isEditMode ? (originalBookingData?.homePickup || false) : (storeHomePickupNeeded ?? DEFAULT_HOME_PICKUP_NEEDED);
  const inStorePickup = isEditMode ? (originalBookingData?.inStorePickup || false) : (storeInStorePickup ?? DEFAULT_IN_STORE_PICKUP);
  const gatedCommunity = isEditMode ? (originalBookingData?.gatedCommunity || false) : (storeGatedCommunity || false);
  const communityName = isEditMode ? (originalBookingData?.communityName || "") : (storeCommunityName || "");
  const addressNotes = isEditMode ? (originalBookingData?.addressNotes || "") : (storeAddressNotes || "");
  const firstName = isEditMode ? (originalBookingData?.firstName || "") : (storeFirstName || "");
  const lastName = isEditMode ? (originalBookingData?.lastName || "") : (storeLastName || "");
  const customerEmail = isEditMode ? (originalBookingData?.customerEmail || "") : (storeCustomerEmail || "");
  const customerPhone = isEditMode ? (originalBookingData?.customerPhone || "") : (storeCustomerPhone || "");
  
  // Parse phone number into 10 digits for the 10-box input
  const getPhoneDigits = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    const digits = cleaned.slice(0, 10).split("");
    // Pad with empty strings to always have 10 elements
    while (digits.length < 10) digits.push("");
    return digits;
  };
  
  const [phoneDigits, setPhoneDigits] = useState(() => getPhoneDigits(customerPhone));
  const phoneDigitsUpdatingRef = React.useRef(false);
  
  // Update phoneDigits when customerPhone changes externally (not from our own updates)
  useEffect(() => {
    if (!phoneDigitsUpdatingRef.current && !isEditMode) {
      const newDigits = getPhoneDigits(customerPhone);
      const currentDigits = phoneDigits.join("");
      const newDigitsStr = newDigits.join("");
      // Only update if the digits actually changed (external change)
      if (currentDigits !== newDigitsStr) {
        setPhoneDigits(newDigits);
      }
    }
    phoneDigitsUpdatingRef.current = false;
  }, [customerPhone, isEditMode]);
  
  // Convert phoneDigits array to formatted phone string
  const formatPhoneFromDigits = (digits) => {
    const cleaned = digits.join("").replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return cleaned;
  };
  
  // Handle phone digit input
  const handlePhoneDigitChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    phoneDigitsUpdatingRef.current = true;
    const newDigits = [...phoneDigits];
    newDigits[index] = value || "";
    setPhoneDigits(newDigits);
    
    // Update the formatted phone in store
    const formatted = formatPhoneFromDigits(newDigits);
    setCustomerPhone(formatted);
    
    // Auto-advance to next box if digit entered
    if (value && index < 9) {
      setTimeout(() => {
        phoneInputRefs.current[index + 1]?.focus();
      }, 0);
    }
  };
  
  // Handle phone digit backspace
  const handlePhoneDigitKeyPress = (index, e) => {
    const key = e.nativeEvent.key;

    if (key === "Backspace" && !phoneDigits[index] && index > 0) {
      // If current box is empty, move to previous and clear it
      phoneDigitsUpdatingRef.current = true;
      const newDigits = [...phoneDigits];
      newDigits[index - 1] = "";
      setPhoneDigits(newDigits);
      const formatted = formatPhoneFromDigits(newDigits);
      setCustomerPhone(formatted);
      setTimeout(() => {
        phoneInputRefs.current[index - 1]?.focus();
      }, 0);
      return;
    }

    if (key === "ArrowLeft" && index > 0) {
      phoneInputRefs.current[index - 1]?.focus();
      return;
    }

    if (key === "ArrowRight" && index < phoneDigits.length - 1) {
      phoneInputRefs.current[index + 1]?.focus();
      return;
    }
  };
  
  const addressInput = isEditMode 
    ? (originalBookingData?.deliveryAddress || "") 
    : (storeAddressInput !== undefined && storeAddressInput !== null ? storeAddressInput : "");
  const addressConfirmed = isEditMode ? (originalBookingData?.deliveryAddress ? true : false) : (storeAddressConfirmed || false);
  const waiverSigned = isEditMode ? (originalBookingData?.waivers?.some(w => w.isOriginalBooker) || false) : (storeWaiverSigned || false);
  const waiverData = isEditMode ? (originalBookingData?.waivers?.find(w => w.isOriginalBooker) || null) : (storeWaiverData || null);

  // Refs
  const scrollViewRef = React.useRef(null);
  const deliveryOptionsRef = React.useRef(null);
  const validateButtonOpacity1 = React.useRef(new Animated.Value(1)).current;
  const validateButtonOpacity2 = React.useRef(new Animated.Value(1)).current;
  
  // Check if validate button should be enabled (address >= 7 characters)
  const isValidateEnabled1 = (deliveryNeeded || homePickupNeeded) && addressInput.trim().length >= 7;
  const isValidateEnabled2 = (deliveryNeeded || homePickupNeeded) && addressInput.trim().length >= 7;
  
  // Animate validate button 1 (first address input)
  React.useEffect(() => {
    if (isValidateEnabled1) {
      const blinkAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(validateButtonOpacity1, {
            toValue: 0.3,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(validateButtonOpacity1, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      );
      blinkAnimation.start();
      return () => blinkAnimation.stop();
    } else {
      validateButtonOpacity1.setValue(1);
    }
  }, [isValidateEnabled1, validateButtonOpacity1]);
  
  // Animate validate button 2 (second address input)
  React.useEffect(() => {
    if (isValidateEnabled2) {
      const blinkAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(validateButtonOpacity2, {
            toValue: 0.3,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(validateButtonOpacity2, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      );
      blinkAnimation.start();
      return () => blinkAnimation.stop();
    } else {
      validateButtonOpacity2.setValue(1);
    }
  }, [isValidateEnabled2, validateButtonOpacity2]);
  
  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ";
  
  // Map refs for address validation modal
  const mapRef1 = React.useRef(null);
  const mapInstance1 = React.useRef(null);
  const directionsService1 = React.useRef(null);
  const directionsRenderer1 = React.useRef(null);
  const [mapsLoaded, setMapsLoaded] = React.useState(false);
  
  // Load Google Maps script
  React.useEffect(() => {
    if (window.google && window.google.maps) {
      setMapsLoaded(true);
      return;
    }
    
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`
    );
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          setMapsLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return;
    }
    
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry,directions`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.maps) {
        setMapsLoaded(true);
      }
    };
    document.head.appendChild(script);
  }, []);
  
  // Initialize map with route when modal opens
  React.useEffect(() => {
    if (!showAddressValidationModal || !mapsLoaded || !addressInput) {
      // Clean up when modal closes
      if (!showAddressValidationModal && mapInstance1.current) {
        mapInstance1.current = null;
        directionsRenderer1.current = null;
      }
      return;
    }
    
    const initializeMap = () => {
      // Wait for DOM element to be ready with dimensions
      if (!mapRef1.current) {
        console.log("Map ref not ready");
        return;
      }
      
      const mapElement = mapRef1.current;
      if (!mapElement.offsetWidth || !mapElement.offsetHeight) {
        console.log("Map element has no dimensions:", mapElement.offsetWidth, mapElement.offsetHeight);
        return;
      }
      
      // Get shop address from settings
      const shopAddress = startingAddress || "9201 Bonita Beach Rd SE, Bonita Springs, FL 34135";
      const destinationAddress = addressInput ? addressInput.trim() : "";
      
      if (!destinationAddress) {
        console.error("❌ No destination address provided for map");
        setMapError("No address provided");
        return;
      }
      
      console.log("🗺️ Initializing map route:", { shopAddress, destinationAddress });
      
      // Initialize map
      const map = new window.google.maps.Map(mapElement, {
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
      });
      mapInstance1.current = map;
      
      // Initialize directions service and renderer
      const directionsService = new window.google.maps.DirectionsService();
      const directionsRenderer = new window.google.maps.DirectionsRenderer({
        map: map,
        suppressMarkers: false,
      });
      directionsService1.current = directionsService;
      directionsRenderer1.current = directionsRenderer;
      
      // Request route
      directionsService.route(
        {
          origin: shopAddress,
          destination: destinationAddress,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK") {
            directionsRenderer.setDirections(result);
            // Fit bounds to show entire route
            const bounds = new window.google.maps.LatLngBounds();
            result.routes[0].legs.forEach((leg) => {
              bounds.extend(leg.start_location);
              bounds.extend(leg.end_location);
            });
            map.fitBounds(bounds);
            
            // Trigger resize event to ensure map renders correctly
            setTimeout(() => {
              window.google.maps.event.trigger(map, "resize");
            }, 100);
          } else {
            console.error("Directions request failed:", status);
            setMapError("Could not calculate route");
          }
        }
      );
    };
    
    // Wait longer for modal to fully render, then check dimensions
    const checkAndInitialize = () => {
      if (!mapRef1.current) {
        setTimeout(checkAndInitialize, 100);
        return;
      }
      
      const mapElement = mapRef1.current;
      if (mapElement.offsetWidth > 0 && mapElement.offsetHeight > 0) {
        initializeMap();
      } else {
        // Retry after a short delay if dimensions aren't ready
        setTimeout(checkAndInitialize, 100);
      }
    };
    
    const timer = setTimeout(checkAndInitialize, 300);
    return () => {
      clearTimeout(timer);
      // Clean up when modal closes
      if (directionsRenderer1.current) {
        directionsRenderer1.current.setMap(null);
      }
    };
  }, [showAddressValidationModal, mapsLoaded, addressInput, startingAddress]);

  // When address validation modal opens, dismiss keyboard and blur so address hints dropdown closes
  // (otherwise the first tap on Confirm closes the dropdown and a second tap is needed)
  useEffect(() => {
    if (showAddressValidationModal) {
      Keyboard.dismiss();
      if (typeof document !== "undefined" && document.activeElement?.blur) {
        document.activeElement.blur();
      }
    }
  }, [showAddressValidationModal]);

  // Handler for Validate button click
  const handleValidateAddress = async (addressIndex) => {
    const currentAddress = addressInput ? addressInput.trim() : "";
    if (!currentAddress || currentAddress.length < 7) return;
    
    console.log("📍 Validating address:", currentAddress);
    
    setMapLoading(true);
    setMapError(null);
    setShowAddressValidationModal(true);
    
    try {
      // Call Firebase function to get distance
      const getDistance = httpsCallable(functions, "getDistance");
      const result = await getDistance({
        fullAddress: currentAddress,
      });
      
      console.log("📏 Distance calculation result:", result.data);
      
      if (result.data?.miles !== null && result.data?.miles !== undefined) {
        setAddressDistanceMiles(parseFloat(result.data.miles.toFixed(1)));
      } else {
        console.error("❌ No miles returned in result:", result.data);
        setMapError("Could not calculate distance");
      }
    } catch (error) {
      console.error("❌ Error calculating distance:", error);
      setMapError(error.message || "Failed to calculate distance");
    } finally {
      setMapLoading(false);
    }
  };
  
  // Handler for Confirm button
  const handleConfirmAddress = () => {
    setAddressConfirmed(true);
    setShowAddressValidationModal(false);
    setAddressDistanceMiles(null);
    setMapError(null);
  };
  
  // Handler for Wrong button
  const handleWrongAddress = () => {
    setAddressConfirmed(false);
    setShowAddressValidationModal(false);
    setAddressDistanceMiles(null);
    setMapError(null);
    // Show informational modal after a brief delay
    setTimeout(() => {
      setShowAddressWrongModal(true);
    }, 300);
  };
  
  const addressSectionRef = React.useRef(null);
  const bicyclesSectionRef = React.useRef(null);
  const customerInfoRef = React.useRef(null);
  const orderSummaryRef = React.useRef(null);

  // Cost
  const [rentalCost, setRentalCost] = useState(null);
  const [calculatingCost, setCalculatingCost] = useState(false);
  // Duration discount preview based only on selected dates
  const [durationDiscountPreview, setDurationDiscountPreview] = useState(null);
  
  // Discount unlock modal state
  const [discountUnlockModalVisible, setDiscountUnlockModalVisible] = useState(false);
  const [unlockedDiscountType, setUnlockedDiscountType] = useState(null); // "transportation" or "duration"
  const [unlockedDiscountName, setUnlockedDiscountName] = useState("");
  const [unlockedDiscountPercent, setUnlockedDiscountPercent] = useState(0);
  const [shownTransportationUnlock, setShownTransportationUnlock] = useState(false);
  const [shownDurationUnlock, setShownDurationUnlock] = useState(false);
  const previousTransportationDiscountRef = React.useRef(null);
  const previousDurationDiscountRef = React.useRef(null);
  const hasInitializedDiscountsRef = React.useRef(false);

  const needsAddress = deliveryNeeded || homePickupNeeded;
  const hasAddress = addressInput.trim();
  const hasMinimumCharacters = addressInput.trim().length >= 7;
  const addressRequirementMet = !needsAddress || (hasAddress && hasMinimumCharacters);
  const shouldShowCheckoutSection = !needsAddress || hasMinimumCharacters || isEditMode;

  // Determine in-store pickup label and state based on delivery/homePickup selections
  const getInStorePickupLabel = () => {
    if (inStorePickup && deliveryNeeded && !homePickupNeeded) {
      // In-store pickup AND delivery needed are both checked
      return "Customer Dropoff";
    } else if (inStorePickup && !deliveryNeeded && !homePickupNeeded) {
      // In-store pickup is ONLY selected (neither delivery nor home pickup)
      return "In-Store Pickup/Dropoff";
    } else if (inStorePickup && !deliveryNeeded && homePickupNeeded) {
      // Home pickup AND in-store pickup are selected
      return "In-Store Pickup";
    } else if (deliveryNeeded && homePickupNeeded) {
      // Both delivery and homePickup checked (in-store pickup would be false in this case)
      return "In-Store Pickup & Dropoff";
    } else if (deliveryNeeded && !homePickupNeeded) {
      // Only delivery checked (in-store pickup would be false in this case)
      return "In-Store Dropoff";
    } else {
      // Default fallback
      return "In-Store Pickup";
    }
  };

  // Determine if in-store pickup radio should be checked/selected
  const shouldInStorePickupBeChecked = () => {
    if (deliveryNeeded && homePickupNeeded) {
      // Both checked - radio should be unchecked
      return false;
    } else if (deliveryNeeded && !homePickupNeeded) {
      // Only delivery - radio should be checked
      return true;
    } else if (!deliveryNeeded && homePickupNeeded) {
      // Only homePickup - radio should be checked
      return true;
    } else {
      // Neither checked - radio state based on inStorePickup state
      return inStorePickup;
    }
  };

  // Removed derived loading effect
  // Removed sync useEffect - handlers update store directly, no need to sync

  useEffect(() => {
    if (isEditMode && (deliveryNeeded || homePickupNeeded)) {
      setAddressConfirmed(true);
    }
  }, [isEditMode, deliveryNeeded, homePickupNeeded]);

  // Auto-sync inStorePickup state based on delivery/homePickup selections
  useEffect(() => {
    if (deliveryNeeded && homePickupNeeded) {
      // Both delivery and home pickup checked - uncheck in-store pickup
      setInStorePickup(false);
    } else {
      // All other cases - in-store pickup MUST be checked
      // (delivery only, home pickup only, or neither)
      setInStorePickup(true);
    }
  }, [deliveryNeeded, homePickupNeeded]);

  // Removed fake loading progress effect

  // Removed settings sync effect

  // Check for waiver signed state from navigation
  useEffect(() => {
    if (location.state?.waiverSigned) {
      setWaiverSigned(true);
      if (location.state?.waiverData) {
        setWaiverData(location.state.waiverData);
      }
    }
    
    // Handle prefilled data from edit/refund page
    if (location.state?.prefillData) {
      const prefill = location.state.prefillData;
      
      if (prefill.firstName) setFirstName(prefill.firstName);
      if (prefill.lastName) setLastName(prefill.lastName);
      if (prefill.customerEmail) setEmail(prefill.customerEmail);
      if (prefill.customerPhone) setCustomerPhone(prefill.customerPhone);
      if (prefill.deliveryAddress) {
        setAddressInput(prefill.deliveryAddress);
      }
    }
  }, [location.state]);

  // Check authentication status - handled by store

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Shop contact info is now loaded in the settings useEffect above (no redundant DB calls)

  // Check for edit mode and load existing booking
  useEffect(() => {
    const loadBookingForEdit = async () => {
      // TODO: Replace with actual check - for now using placeholder
      const editBookingId = location.state?.editBookingId || null;

      if (editBookingId) {
        setIsEditMode(true);
        setOriginalBookingId(editBookingId);

        try {
          // Load booking from Zustand store (already synced by real-time listener)
          let bookingData = bookingsFromStore.find(b => 
            b.id === editBookingId ||
            b.bookingId === editBookingId
          );

          // If not in state, try to find in store
          if (!bookingData) {
            if (bookingsFromStore && bookingsFromStore.length > 0) {
              bookingData = bookingsFromStore.find(b => b.id === editBookingId || b.bookingId === editBookingId);
            }
          }

          if (bookingData) {
            setOriginalBookingData(bookingData);


            // Populate form fields with existing data
            setFirstName(capitalizeWords(bookingData.firstName || ""));
            setLastName(capitalizeWords(bookingData.lastName || ""));
            setCustomerEmail(bookingData.customerEmail || "");
            setCustomerPhone(
              formatPhoneNumber(bookingData.customerPhone || "")
            );
            setAddressInput(bookingData.deliveryAddress || "");

            // Set dates
            if (bookingData.startDate) {
              const start = bookingData.startDate.toDate
                ? bookingData.startDate.toDate()
                : new Date(bookingData.startDate);
              start.setHours(0, 0, 0, 0);
              setStartDate(start);
            }
            if (bookingData.endDate) {
              const end = bookingData.endDate.toDate
                ? bookingData.endDate.toDate()
                : new Date(bookingData.endDate);
              end.setHours(0, 0, 0, 0);
              setEndDate(end);
            }

            // Set delivery options
            setDeliveryNeeded(bookingData.delivery || false);
            setHomePickupNeeded(bookingData.homePickup || false);
            setInStorePickup(bookingData.inStorePickup || false);
            setGatedCommunity(bookingData.gatedCommunity || false);
            
            // Extract community name from address notes if it exists
            const existingAddressNotes = bookingData.addressNotes || "";
            let extractedCommunityName = "";
            let cleanedAddressNotes = existingAddressNotes;
            
            // Check if address notes contains "Gated community: ..."
            const gatedCommunityMatch = existingAddressNotes.match(/Gated community:\s*(.+?)(?:\n|$)/i);
            if (gatedCommunityMatch && gatedCommunityMatch[1]) {
              extractedCommunityName = gatedCommunityMatch[1].trim();
              // Remove the gated community line from address notes
              cleanedAddressNotes = existingAddressNotes
                .replace(/Gated community:\s*.+?(?:\n|$)/i, "")
                .trim();
            }
            
            setCommunityName(extractedCommunityName);
            setAddressNotes(cleanedAddressNotes);

            // Set bikes - track original quantities
            const product1Items = bookingData.product1;
            if (product1Items && Array.isArray(product1Items)) {
              const bikesObj = {};
              const originalQtys = {};
              product1Items.forEach((bike) => {
                bikesObj[bike.name] = bike.quantity;
                originalQtys[bike.name] = bike.quantity;
              });
              setSelectedBikes(bikesObj);
              setOriginalBicycleQuantities(originalQtys);
            }

            // Set add-ons
            const product2Items = bookingData.product2;
            if (product2Items) {
              setSelectedAddOns(product2Items);
            }

            // Set waiver status
            if (bookingData.waiverSigned) {
              setWaiverSigned(true);
              setWaiverData(bookingData.waiverData || null);
            }

            // Load admin notes
            if (bookingData.adminNotes) {
              setAdminNotes(bookingData.adminNotes);
              // Show admin notes if they exist
              setShowAdminNotes(!!bookingData.adminNotes && bookingData.adminNotes.trim().length > 0);
            }
          } else {
            console.error("❌ Booking not found:", editBookingId);
            alert("Booking not found");
            navigate("/");
          }
        } catch (error) {
          console.error("❌ Error loading booking for edit:", error);
          alert("Error loading booking: " + error.message);
        }
      }
    };

    loadBookingForEdit();
  }, [location.state?.editBookingId]);

  // Auto-save admin notes with debounce
  useEffect(() => {
    // Clear any existing timer
    if (adminNotesTimerRef.current) {
      clearTimeout(adminNotesTimerRef.current);
    }
    
    // Only auto-save if:
    // 1. User is authenticated
    // 2. In edit mode with a valid master booking ID
    // 3. Admin notes have actually changed from original
    if (isAuthenticated && isEditMode && originalBookingId && adminNotes !== originalBookingData?.adminNotes) {
      adminNotesTimerRef.current = setTimeout(async () => {
        try {
          setAdminNotesSaving(true);
          
          // Check existence in store first to avoid unnecessary writes
          const exists = bookingsFromStore.some(b => b.id === originalBookingId || b.bookingId === originalBookingId);
          
          if (exists) {
            await updateDocument("bookings", originalBookingId, {
              adminNotes: adminNotes,
              updatedAt: Timestamp.now()
            });
          }
        } catch (error) {
          console.error("❌ Error auto-saving admin notes:", error);
        } finally {
          setAdminNotesSaving(false);
        }
      }, 2000); // 2 second debounce
    }
    
    // Cleanup function
    return () => {
      if (adminNotesTimerRef.current) {
        clearTimeout(adminNotesTimerRef.current);
      }
    };
  }, [adminNotes, isAuthenticated, isEditMode, originalBookingId, originalBookingData, bookingsFromStore]);

  // Product titles are now loaded in the settings useEffect above (no redundant DB calls)

  // Removed data syncing effects - using direct store access

  // Calculate availability (Computed via useMemo - No Side Effects)
  const { availableBikes, availableAddOns } = React.useMemo(() => {
    if (!startDate || !endDate) {
      return { availableBikes: [], availableAddOns: addOns };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      return { availableBikes: [], availableAddOns: addOns };
    }

    // Filter bookings that overlap with the selected date range
    const overlappingBookings = bookings.filter((booking) => {
      if (!booking.startDate || !booking.endDate) return false;

      let bookingStart, bookingEnd;
      try {
        if (booking.startDate.toDate) {
          bookingStart = booking.startDate.toDate();
        } else if (booking.startDate.seconds) {
          bookingStart = new Date(booking.startDate.seconds * 1000);
        } else if (booking.startDate instanceof Timestamp) {
          bookingStart = booking.startDate.toDate();
        } else if (typeof booking.startDate === 'string') {
          bookingStart = new Date(booking.startDate);
        } else {
          bookingStart = new Date(booking.startDate);
        }

        if (booking.endDate.toDate) {
          bookingEnd = booking.endDate.toDate();
        } else if (booking.endDate.seconds) {
          bookingEnd = new Date(booking.endDate.seconds * 1000);
        } else if (booking.endDate instanceof Timestamp) {
          bookingEnd = booking.endDate.toDate();
        } else if (typeof booking.endDate === 'string') {
          bookingEnd = new Date(booking.endDate);
        } else {
          bookingEnd = new Date(booking.endDate);
        }

        bookingStart.setHours(0, 0, 0, 0);
        bookingEnd.setHours(0, 0, 0, 0);
        const searchStart = new Date(start);
        const searchEnd = new Date(end);
        searchStart.setHours(0, 0, 0, 0);
        searchEnd.setHours(0, 0, 0, 0);

        return bookingStart <= searchEnd && bookingEnd >= searchStart;
      } catch (error) {
        return false;
      }
    });

    // Calculate booked quantities per bike
    const bookedQuantities = {};
    inventory.forEach((bike) => { bookedQuantities[bike.name] = 0; });

    // Calculate booked quantities per add-on
    const bookedAddOnQuantities = {};
    addOns.forEach((addOn) => {
      if (addOn.inventory !== null && addOn.inventory !== undefined) {
        bookedAddOnQuantities[addOn.id] = 0;
      }
    });

    overlappingBookings.forEach((booking) => {
      const product1Items = booking.product1;
      if (product1Items && Array.isArray(product1Items)) {
        product1Items.forEach((bike) => {
          if (bookedQuantities.hasOwnProperty(bike.name)) {
            const qty = bike.quantity && bike.quantity > 0 ? bike.quantity : 1;
            bookedQuantities[bike.name] += qty;
          }
        });
      }
      
      const product2Items = booking.product2;
      if (product2Items) {
        Object.entries(product2Items).forEach(([addOnId, value]) => {
          if (bookedAddOnQuantities.hasOwnProperty(addOnId)) {
            const addOn = addOns.find(a => a.id === addOnId);
            if (addOn && addOn.option === 'user-selection' && value?.quantity) {
              bookedAddOnQuantities[addOnId] += value.quantity || 0;
            }
          }
        });
      }
    });

    const availability = inventory.map((bike) => {
      const booked = bookedQuantities[bike.name] || 0;
      const available = Math.max(0, bike.quantity - booked);
      return { ...bike, booked, available };
    });

    const updatedAddOns = addOns.map((addOn) => {
      if (addOn.inventory !== null && addOn.inventory !== undefined) {
        const booked = bookedAddOnQuantities[addOn.id] || 0;
        const available = Math.max(0, addOn.inventory - booked);
        return { ...addOn, booked, available, originalInventory: addOn.inventory };
      }
      return addOn;
    });

    return { availableBikes: availability, availableAddOns: updatedAddOns };
  }, [startDate, endDate, inventory, bookings, addOns]);

  // Default first available bike removed - start with empty booking

  // Helper: update duration discount preview based on a tentative start/end
  const updateDurationDiscountPreview = (previewStart, previewEnd) => {
    if (!previewStart || !previewEnd) {
      setDurationDiscountPreview(null);
      return;
    }

    try {
      const result = calculateDurationDiscountUtil({
        booking: {
          product1: [],
          product2: [],
          startDate: previewStart,
          endDate: previewEnd,
        },
        durationDiscounts,
        products1Catalog: inventory,
        products2Catalog: addOns,
        useDurationDiscounts,
      });

      if (result.discountPercent > 0 && result.discountName) {
        setDurationDiscountPreview({
          discountPercent: result.discountPercent,
          discountName: result.discountName,
        });
      } else {
        setDurationDiscountPreview(null);
      }
    } catch (e) {
      console.error("❌ Error calculating duration discount preview:", e);
      setDurationDiscountPreview(null);
    }
  };

  // Handle date selection in calendar
  const handleDateSelect = (date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (!startDate) {
      // No dates set yet - set start date
      setStartDate(selectedDate);
      setEndDate(null);
      setDurationDiscountPreview(null);
    } else if (startDate && !endDate) {
      // Only start date set - set end date (must be a different day for 1 full day minimum)
      if (selectedDate > startDate) {
        setEndDate(selectedDate);
        updateDurationDiscountPreview(startDate, selectedDate);
      } else if (selectedDate.getTime() < startDate.getTime()) {
        // If selected date is before start, make it the new start
        setStartDate(selectedDate);
        setEndDate(null);
        setDurationDiscountPreview(null);
      }
      // If same day as start, do nothing — user must select 2 different squares
    } else if (startDate && endDate) {
      // Both dates already set - smart update
      if (
        selectedDate.getTime() === startDate.getTime() ||
        selectedDate.getTime() === endDate.getTime()
      ) {
        // Clicked either start or end date - clear selection to restart
        setStartDate(null);
        setEndDate(null);
        setDurationDiscountPreview(null);
      } else if (selectedDate > startDate) {
        // Clicked after start - update end date only
        setEndDate(selectedDate);
        updateDurationDiscountPreview(startDate, selectedDate);
      } else {
        // Clicked before start - update start date only
        setStartDate(selectedDate);
        updateDurationDiscountPreview(selectedDate, endDate);
      }
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      const dayAbbr = d.toLocaleDateString("en-US", { weekday: "short" }); // Mon, Tue, etc.
      const monthAbbr = d.toLocaleDateString("en-US", { month: "short" }); // Jan, Feb, etc.
      const day = d.getDate(); // 15, 16, etc.
      const year = d.getFullYear().toString().slice(-2); // 25, 26, etc. (last 2 digits)
      return `${dayAbbr} ${monthAbbr} ${day}, '${year}`;
    } catch {
      return "N/A";
    }
  };

  // Helper function to get day abbreviation
  const getDayAbbreviation = (date) => {
    const dayIndex = date.getDay();
    const abbreviations = ['sun', 'mon', 'tue', 'wed', 'thurs', 'fri', 'sat'];
    return abbreviations[dayIndex];
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  // Navigate to next month
  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  // Check if a date is in the selected range
  const isInRange = (date) => {
    if (!startDate || !endDate) return false;
    const dateTime = date.getTime();
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    return dateTime >= startTime && dateTime <= endTime;
  };

  // Handle bike selection
  const handleBikeSelect = (bikeName, quantity) => {
    // In edit mode, prevent decreasing quantities below original
    if (isEditMode) {
      const originalQty = originalBicycleQuantities[bikeName] || 0;
      if (quantity < originalQty) {
        return; // Prevent decreasing
      }
    }

    if (quantity === 0) {
      const updated = { ...selectedBikes };
      delete updated[bikeName];
      setSelectedBikes(updated);
    } else {
      setSelectedBikes({ ...selectedBikes, [bikeName]: quantity });
    }
  };

  const handleAddOnSelect = (addOnId, quantity, isPerBike = false, isPerBooking = false) => {
    if (isPerBike) {
      // For per-bike, it's a boolean toggle
      if (quantity === 0) {
        const updated = { ...selectedAddOns };
        delete updated[addOnId];
        setSelectedAddOns(updated);
      } else {
        setSelectedAddOns({ ...selectedAddOns, [addOnId]: true });
      }
    } else {
      // For per-booking or user-selection, it's a quantity object
      if (quantity === 0) {
        if (isPerBooking) {
          const updated = { ...selectedAddOns };
          delete updated[addOnId];
          setSelectedAddOns(updated);
        } else {
          const updated = { ...selectedAddOns };
          delete updated[addOnId];
          setSelectedAddOns(updated);
        }
      } else {
        setSelectedAddOns({ ...selectedAddOns, [addOnId]: { quantity } });
      }
    }
  };

  // Distance state
  const [distanceMiles, setDistanceMiles] = useState(null);

  // Calculate rental cost when dates and selections change
  // Memoize stringified versions to avoid infinite loops
  const selectedBikesStr = React.useMemo(() => JSON.stringify(selectedBikes), [selectedBikes]);
  const selectedAddOnsStr = React.useMemo(() => JSON.stringify(selectedAddOns), [selectedAddOns]);
  const inventoryStr = React.useMemo(() => JSON.stringify(inventory), [inventory]);
  const addOnsStr = React.useMemo(() => JSON.stringify(addOns), [addOns]);
  const deliveryDiscountsStr = React.useMemo(() => JSON.stringify(deliveryDiscounts), [deliveryDiscounts]);
  const durationDiscountsStr = React.useMemo(() => JSON.stringify(durationDiscounts), [durationDiscounts]);
  
  // Use ref to track previous dependency values to prevent unnecessary recalculations
  const prevDepsRef = React.useRef({
    selectedBikesStr: '',
    selectedAddOnsStr: '',
    inventoryStr: '',
    addOnsStr: '',
    startDate: null,
    endDate: null,
    addressInput: '',
    deliveryNeeded: false,
    homePickupNeeded: false,
    deliveryDiscountsStr: '',
    durationDiscountsStr: '',
  });
  
  // Use ref to track if calculation is in progress to prevent loops
  const isCalculatingRef = React.useRef(false);
  
  useEffect(() => {
    // Prevent concurrent calculations
    if (isCalculatingRef.current) {
      return;
    }
    
    // Check if any relevant dependency actually changed
    const currentDeps = {
      selectedBikesStr,
      selectedAddOnsStr,
      inventoryStr,
      addOnsStr,
      startDate: startDate?.getTime(),
      endDate: endDate?.getTime(),
      addressInput,
      deliveryNeeded,
      homePickupNeeded,
      startingAddress,
      mileageRate,
      loadingFee,
      useDeliveryDiscounts,
      deliveryDiscountsStr,
      useDurationDiscounts,
      durationDiscountsStr,
    };
    
    const prevDeps = prevDepsRef.current;
    const hasChanged = 
      currentDeps.selectedBikesStr !== prevDeps.selectedBikesStr ||
      currentDeps.selectedAddOnsStr !== prevDeps.selectedAddOnsStr ||
      currentDeps.inventoryStr !== prevDeps.inventoryStr ||
      currentDeps.addOnsStr !== prevDeps.addOnsStr ||
      currentDeps.startDate !== prevDeps.startDate ||
      currentDeps.endDate !== prevDeps.endDate ||
      currentDeps.addressInput !== prevDeps.addressInput ||
      currentDeps.deliveryNeeded !== prevDeps.deliveryNeeded ||
      currentDeps.homePickupNeeded !== prevDeps.homePickupNeeded ||
      currentDeps.startingAddress !== prevDeps.startingAddress ||
      currentDeps.mileageRate !== prevDeps.mileageRate ||
      currentDeps.loadingFee !== prevDeps.loadingFee ||
      currentDeps.useDeliveryDiscounts !== prevDeps.useDeliveryDiscounts ||
      currentDeps.deliveryDiscountsStr !== prevDeps.deliveryDiscountsStr ||
      currentDeps.useDurationDiscounts !== prevDeps.useDurationDiscounts ||
      currentDeps.durationDiscountsStr !== prevDeps.durationDiscountsStr;
    
    if (!hasChanged) {
      return; // Skip if nothing changed
    }
    
    // Update ref with current values
    prevDepsRef.current = currentDeps;
    
    const calculateRentalCost = async () => {
      // Only require address if delivery or home pickup is needed
      const needsAddress = deliveryNeeded || homePickupNeeded;
      if (
        !startDate ||
        !endDate ||
        (needsAddress && !addressInput.trim()) ||
        Object.keys(selectedBikes).length === 0
      ) {
        setRentalCost(null);
        setDistanceMiles(null);
        return;
      }

      const selectedEntries = Object.entries(selectedBikes).filter(
        ([_, qty]) => qty > 0
      );
      if (selectedEntries.length === 0) {
        setRentalCost(null);
        setDistanceMiles(null);
        return;
      }

      try {
        setCalculatingCost(true);

        // Calculate days — midnight-to-midnight, rounded (same as getRentalDays:
        // round absorbs the DST hour that made ceil bill fall-back ranges a day long)
        const start = new Date(startDate);
        const end = new Date(endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 1;

        // Calculate total bikes
        const numBikes = selectedEntries.reduce(
          (sum, [_, qty]) => sum + qty,
          0
        );

        // Calculate bicycle rental cost in CENTS (prices are stored in cents)
        let rentalCostGrossCents = 0;
        selectedEntries.forEach(([bikeName, qty]) => {
          const bike = inventory.find((b) => b.name === bikeName);
          if (bike) {
            // effectiveBikePriceCents(bike) is in cents
            rentalCostGrossCents += effectiveBikePriceCents(bike) * qty * days;
          }
        });

        // Add add-ons cost in CENTS
        Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
          const addOn = addOns.find((a) => a.id === addOnId);
          if (addOn) {
            // addOn.price is in cents
            if (addOn.option === "per-booking" && value?.quantity) {
              rentalCostGrossCents += addOn.price * value.quantity;
            } else if (addOn.option === "per-bike" && value === true) {
              // For per-bike, multiply by total number of product1 items
              rentalCostGrossCents += addOn.price * numBikes;
            } else if (addOn.option === "user-selection" && value?.quantity) {
              // Billed per day, matching the engine/EditBooking/discount util
              rentalCostGrossCents += addOn.price * value.quantity * days;
            }
          }
        });

        // Calculate distance and delivery costs if address is provided
        // Use cached distance if available (from previous calculation)
        let distance = distanceMiles; // Start with cached distance
        let deliveryApplied = 0;
        let pickupApplied = 0;

        // mileageRate and loadingFee are ALREADY stored in CENTS in the database
        // Do NOT convert them - use them directly
        const mileageRateCents = Number(mileageRate) || 0;
        const loadingFeeCents = Number(loadingFee) || 0;

        if (needsAddress && addressInput.trim()) {
          // Use startingAddress if available, otherwise default to shop address
          const originAddress =
            startingAddress ||
            "9102 Bonita Beach Rd SE, Bonita Springs, FL 34135";

          try {
            const getDistanceFn = httpsCallable(functions, "getDistance");
            const result = await getDistanceFn({
              fullAddress: addressInput.trim(),
            });

            if (
              result.data &&
              (result.data.distanceMiles !== undefined ||
                result.data.miles !== undefined)
            ) {
              // Round to 1 decimal like every other surface (wizard, calculator,
              // StepTransportation) so the same address prices identically everywhere.
              distance = Number(
                Number(result.data.distanceMiles ?? result.data.miles).toFixed(1)
              );
              setDistanceMiles(distance);

              // Calculate per-direction cost in CENTS
              
              // Formula: (distance * mileageRate) + loadingFee for each direction (all in cents)
              const deliveryCostPerDirectionCents =
                distance > 0 && mileageRate > 0
                  ? Math.round(distance * mileageRateCents) + loadingFeeCents
                  : loadingFeeCents;

              // Apply loading fee for each selected checkbox option (in cents)
              // Delivery Needed checkbox
              if (deliveryNeeded && distance > 0) {
                deliveryApplied = deliveryCostPerDirectionCents;
              } else if (deliveryNeeded) {
                deliveryApplied = loadingFeeCents;
              } else {
                deliveryApplied = 0; // Explicitly set to 0 if not needed
              }

              // Home Pickup Needed checkbox
              if (homePickupNeeded && distance > 0) {
                pickupApplied = deliveryCostPerDirectionCents;
              } else if (homePickupNeeded) {
                pickupApplied = loadingFeeCents;
              } else {
                pickupApplied = 0; // Explicitly set to 0 if not needed
              }
            }
          } catch (error) {
            console.error("❌ Error calculating distance:", error);
            setDistanceMiles(null);
          }
        } else {
          // If address block not entered, but we have a cached distance, use it
          // If we have a cached distance and delivery/pickup is selected, calculate costs
          if (distanceMiles !== null && distanceMiles !== undefined && (deliveryNeeded || homePickupNeeded)) {
            distance = distanceMiles;
            
            // Calculate per-direction cost in CENTS using cached distance
            const deliveryCostPerDirectionCents =
              distance > 0 && mileageRate > 0
                ? Math.round(distance * mileageRateCents) + loadingFeeCents
                : loadingFeeCents;

            // Apply loading fee for each selected checkbox option (in cents)
            if (deliveryNeeded && distance > 0) {
              deliveryApplied = deliveryCostPerDirectionCents;
            } else if (deliveryNeeded) {
              deliveryApplied = loadingFeeCents;
            } else {
              deliveryApplied = 0;
            }

            if (homePickupNeeded && distance > 0) {
              pickupApplied = deliveryCostPerDirectionCents;
            } else if (homePickupNeeded) {
              pickupApplied = loadingFeeCents;
            } else {
              pickupApplied = 0;
            }
          } else {
            // No cached distance and no address - reset to 0
            if (!deliveryNeeded) {
              deliveryApplied = 0;
            }
            if (!homePickupNeeded) {
              pickupApplied = 0;
            }
          }
        }

        // Calculate delivery discount (if enabled and applicable)
        // NOTE: Discount tiers are in DOLLARS, so convert rentalCostGrossCents to dollars for comparison
        let deliveryDiscountPercent = 0;
        let deliveryDiscountAmountCents = 0;
        let deliveryDiscountId = null;
        let deliveryDiscountName = '';

        // Apply delivery discount based on PRODUCT DOLLAR AMOUNT (not mileage)
        if (useDeliveryDiscounts && deliveryDiscounts.length > 0 && (deliveryNeeded || homePickupNeeded)) {
          // Match tiers in CENTS. centsToDollars() returns strings, and string
          // comparison is lexicographic ("250.00" <= "99.99" is true), which
          // mis-tiered every order from $100 up.
          const applicableDiscounts = deliveryDiscounts.filter(discount => {
            return rentalCostGrossCents >= (discount.from || 0) && rentalCostGrossCents <= (discount.to || 0);
          });
          
          // Apply the HIGHEST discount percentage if multiple match
          if (applicableDiscounts.length > 0) {
            const highestDiscount = applicableDiscounts.reduce((max, discount) => {
              return (discount.value > max.value) ? discount : max;
            }, applicableDiscounts[0]);
            
            deliveryDiscountPercent = highestDiscount.value || 0;
            deliveryDiscountId = highestDiscount.id || null;
            deliveryDiscountName = highestDiscount.name || '';
            
            // Calculate discount amount in CENTS (deliveryApplied and pickupApplied are in cents)
            const deliveryPickupTotalCents = deliveryApplied + pickupApplied;
            deliveryDiscountAmountCents = Math.round(deliveryPickupTotalCents * (deliveryDiscountPercent / 100));
          }
        }

        // Calculate duration discount (if enabled and applicable) - in CENTS
        // Use shared utility function
        // Override already IS the negotiated price; stacking a % discount on top
      // would undercharge. Also, the util derives its base from catalog prices,
      // so it would discount against the wrong number anyway.
      const durationDiscountResult = hasRateOverride
        ? { discountAmountCents: 0, discountPercent: 0, discountName: '' }
        : calculateDurationDiscountUtil({
          booking: {
            product1: Object.entries(selectedBikes).map(([bikeName, quantity]) => {
              const bike = inventory.find((b) => b.name === bikeName);
              return bike ? { id: bike.id, quantity } : null;
            }).filter(Boolean),
            product2: Object.entries(selectedAddOns).map(([addOnId, value]) => {
              const addOn = addOns.find((a) => a.id === addOnId);
              if (!addOn) return null;
              if (addOn.option === "per-booking") {
                return { id: addOnId, quantity: value?.quantity || 0 };
              } else if (addOn.option === "per-bike") {
                return { id: addOnId, quantity: value === true ? 1 : 0 };
              } else if (addOn.option === "user-selection") {
                return { id: addOnId, quantity: value?.quantity || 0 };
              }
              return null;
            }).filter(Boolean),
            startDate,
            endDate,
          },
          durationDiscounts,
          products1Catalog: inventory,
          products2Catalog: addOns,
          useDurationDiscounts,
        });
        
        const durationDiscountPercent = durationDiscountResult.discountPercent;
        const durationDiscountAmountCents = durationDiscountResult.discountAmountCents;
        const durationDiscountId = durationDiscountResult.discountId;
        const durationDiscountName = durationDiscountResult.discountName;

        // Calculate net costs after discounts (all in CENTS)
        const rentalCostNetCents = rentalCostGrossCents - durationDiscountAmountCents; // Equipment rental after duration discount
        const deliveryPickupTotalCents = deliveryApplied + pickupApplied;
        const deliveryPickupNetCents = deliveryPickupTotalCents - deliveryDiscountAmountCents; // Delivery/pickup after delivery discount
        
        // Calculate individual discounted amounts for delivery and pickup (proportional to their base costs)
        let deliveryWithDiscountCents = 0;
        let pickupWithDiscountCents = 0;
        if (deliveryPickupTotalCents > 0) {
          // Apply discount proportionally based on each service's share of the total
          const deliveryProportion = deliveryApplied / deliveryPickupTotalCents;
          const pickupProportion = pickupApplied / deliveryPickupTotalCents;
          const deliveryDiscountShare = Math.round(deliveryDiscountAmountCents * deliveryProportion);
          const pickupDiscountShare = Math.round(deliveryDiscountAmountCents * pickupProportion);
          deliveryWithDiscountCents = deliveryApplied - deliveryDiscountShare;
          pickupWithDiscountCents = pickupApplied - pickupDiscountShare;
        } else {
          // If no delivery/pickup total, keep original values
          deliveryWithDiscountCents = deliveryApplied;
          pickupWithDiscountCents = pickupApplied;
        }
        
        // Total cost in CENTS = rental + discounted delivery/pickup
        const totalCostCents = rentalCostNetCents + deliveryPickupNetCents;
        
        // Store all values in CENTS - convert to dollars ONLY for display
        const totalCost = centsToDollars(totalCostCents);

        // Build rental cost object matching the expected structure
        setRentalCost({
          origin: startingAddress,
          destination: addressInput.trim(),
          miles: distance,
          days: days,
          numBikes: numBikes,
          delivery: deliveryNeeded,
          pickup: homePickupNeeded,
          rates: {
            dailyRentalRate: 0, // Not used, we calculate from inventory
            perMileDeliveryRate: mileageRate,
            discounts: {},
            loadingFee: loadingFee,
          },
          rentalCost: {
            gross: rentalCostGrossCents, // Store in CENTS
            discountPercent: deliveryDiscountPercent,
            discountAmount: deliveryDiscountAmountCents, // Store in CENTS
            net: rentalCostNetCents, // Store in CENTS
            durationDiscountPercent: durationDiscountPercent,
            durationDiscountAmount: durationDiscountAmountCents, // Store in CENTS
          },
          deliveryCost: {
            perDirection: distance
              ? centsToDollars(Math.round(distance * mileageRateCents))
              : 0,
            deliveryApplied: centsToDollars(deliveryApplied),
            pickupApplied: centsToDollars(pickupApplied),
            totalApplied: centsToDollars(deliveryApplied + pickupApplied),
          },
          totalCost: totalCost,
          // Store all values in CENTS
          deliveryApplied: deliveryApplied, // in cents
          pickupApplied: pickupApplied, // in cents
          discountAmount: deliveryDiscountAmountCents, // in cents
          discountPercent: deliveryDiscountPercent,
          durationDiscountAmount: durationDiscountAmountCents, // in cents
          durationDiscountPercent: durationDiscountPercent,
          // Store discounted amounts (what customer actually paid)
          deliveryWithDiscount: deliveryWithDiscountCents, // in cents
          pickupWithDiscount: pickupWithDiscountCents, // in cents
          
          // DEBUG: Log what we're storing
          _debug_storage: {
            deliveryApplied_cents: deliveryApplied,
            pickupApplied_cents: pickupApplied,
            discountAmount_cents: deliveryDiscountAmountCents,
            durationDiscountAmount_cents: durationDiscountAmountCents,
            deliveryApplied_dollars: centsToDollars(deliveryApplied),
            pickupApplied_dollars: centsToDollars(pickupApplied),
            discountAmount_dollars: centsToDollars(deliveryDiscountAmountCents),
            durationDiscountAmount_dollars: centsToDollars(durationDiscountAmountCents)
          },
          // Discount objects for new structure
          deliveryDiscountObj: {
            discountPercent: deliveryDiscountPercent,
            discountName: deliveryDiscountName,
            discountCents: deliveryDiscountAmountCents, // Already in CENTS
          },
          durationDiscountObj: {
            discountPercent: durationDiscountPercent,
            discountName: durationDiscountName,
            discountCents: durationDiscountAmountCents, // Already in CENTS
          },
        });
      } catch (error) {
        console.error("❌ Error calculating cost:", error);
        setRentalCost(null);
      } finally {
        setCalculatingCost(false);
        isCalculatingRef.current = false;
      }
    };

    calculateRentalCost();
  }, [
    // Use memoized stringified versions to detect actual changes
    selectedBikesStr,
    selectedAddOnsStr,
    inventoryStr,
    addOnsStr,
    startDate?.getTime(),
    endDate?.getTime(),
    addressInput,
    deliveryNeeded,
    homePickupNeeded,
    startingAddress,
    mileageRate,
    loadingFee,
    useDeliveryDiscounts,
    deliveryDiscountsStr,
    useDurationDiscounts,
    durationDiscountsStr,
  ]);

  // Detect higher discount tiers and show modals
  useEffect(() => {
    if (!rentalCost) return;

    const currentTransportationPercent = rentalCost.deliveryDiscountObj?.discountPercent || 0;
    const currentTransportationName = rentalCost.deliveryDiscountObj?.discountName || "";
    const currentDurationPercent = rentalCost.durationDiscountObj?.discountPercent || 0;
    const currentDurationName = rentalCost.durationDiscountObj?.discountName || "";

    // On first load, initialize the refs
    if (!hasInitializedDiscountsRef.current) {
      previousTransportationDiscountRef.current = {
        percent: currentTransportationPercent,
        name: currentTransportationName
      };
      previousDurationDiscountRef.current = {
        percent: currentDurationPercent,
        name: currentDurationName
      };
      hasInitializedDiscountsRef.current = true;
      return; // Don't show modals on initial load
    }

    // Check for higher transportation discount tier
    const transportationChanged = 
      previousTransportationDiscountRef.current?.percent !== currentTransportationPercent ||
      previousTransportationDiscountRef.current?.name !== currentTransportationName;

    if (transportationChanged && 
        currentTransportationPercent > (previousTransportationDiscountRef.current?.percent || 0) && 
        currentTransportationPercent > 0 && 
        !shownTransportationUnlock &&
        !discountUnlockModalVisible) {
      setUnlockedDiscountType("transportation");
      setUnlockedDiscountName(currentTransportationName || "Transportation Discount");
      setUnlockedDiscountPercent(currentTransportationPercent);
      setDiscountUnlockModalVisible(true);
      setShownTransportationUnlock(true);
      // Update ref
      previousTransportationDiscountRef.current = {
        percent: currentTransportationPercent,
        name: currentTransportationName
      };
      return; // Exit early to avoid showing both modals at once
    } else if (transportationChanged) {
      // Update ref even if modal doesn't show
      previousTransportationDiscountRef.current = {
        percent: currentTransportationPercent,
        name: currentTransportationName
      };
    }

    // Check for higher duration discount tier (only if transportation modal is not showing)
    if (!discountUnlockModalVisible) {
      const durationChanged = 
        previousDurationDiscountRef.current?.percent !== currentDurationPercent ||
        previousDurationDiscountRef.current?.name !== currentDurationName;

      if (durationChanged && 
          currentDurationPercent > (previousDurationDiscountRef.current?.percent || 0) && 
          currentDurationPercent > 0 && 
          !shownDurationUnlock) {
        setUnlockedDiscountType("duration");
        setUnlockedDiscountName(currentDurationName || "Duration Discount");
        setUnlockedDiscountPercent(currentDurationPercent);
        setDiscountUnlockModalVisible(true);
        setShownDurationUnlock(true);
        // Update ref
        previousDurationDiscountRef.current = {
          percent: currentDurationPercent,
          name: currentDurationName
        };
      } else if (durationChanged) {
        // Update ref even if modal doesn't show
        previousDurationDiscountRef.current = {
          percent: currentDurationPercent,
          name: currentDurationName
        };
      }
    }
  }, [rentalCost, shownTransportationUnlock, shownDurationUnlock, discountUnlockModalVisible]);

  // Helper function to check if booking is allowed (same-day booking rules)
  const isBookingAllowed = () => {
    if (!startDate) return { allowed: true };

    const now = new Date();
    const currentHour = now.getHours();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingStart = new Date(startDate);
    bookingStart.setHours(0, 0, 0, 0);
    
    // Check if booking is for today
    const isToday = bookingStart.getTime() === today.getTime();
    
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    const isAuthenticated = currentUser !== null;
    
    // Rule: After 10 AM, same-day bookings are only allowed for authenticated users
    if (isToday && currentHour >= 10 && !isAuthenticated) {
      return {
        allowed: false,
        message: "Same-day bookings are not available after 10:00 AM. Please select a start date from tomorrow onwards, or contact us for assistance."
      };
    }
    
    return { allowed: true };
  };

  // Helper function to scroll to a specific section
  const scrollToSection = (refToScroll) => {
    if (!refToScroll || !refToScroll.current) {
      return;
    }

    try {
      // For React Native Web, use the DOM element's scrollIntoView
      const element = refToScroll.current;
      if (element && element.scrollIntoView) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    } catch (error) {
      console.error("Error in scrollToSection:", error);
    }
  };

  // Helper function to check if bicycles have increased (edit mode only)
  const haveBicyclesIncreased = () => {
    if (!isEditMode) return false;

    // Compare current quantities with original
    let increased = false;
    Object.entries(selectedBikes).forEach(([bikeName, newQty]) => {
      const originalQty = originalBicycleQuantities[bikeName] || 0;
      if (newQty > originalQty) {
        increased = true;
      }
    });


    return increased;
  };

  // Admin-negotiated per-unit daily rate. This page prices Product1 in SEVEN places;
  // they must all agree or the quote, the summary and the amount charged diverge.
  const hasRateOverride =
    Number.isFinite(dailyRateOverrideCents) && dailyRateOverrideCents >= 0;
  const effectiveBikePriceCents = (bike) =>
    hasRateOverride ? dailyRateOverrideCents : bike?.price || 0;

  // MANUAL BOOKING: Admin function to bypass standard flow or start it manually
  const handleManualBookingSubmit = async () => {
    if (manualBookingMode === "signPay") {
      handleWaiverFlow();
      return;
    }

    // CASH: save the booking as awaiting cash, then run the waiver. Waiver.js hands off
    // to /cash-payment/:bookingId, where staff take the money at the register and
    // confirm — that confirmation is what completes the booking. No Stripe involved.
    if (manualBookingMode === "signPayCash") {
      try {
        setManualBookingProcessing(true);
        const result = await createBookingData(true, true);
        if (!result) {
          setManualBookingProcessing(false);
          return;
        }
        navigate("/waiver", {
          state: {
            customerName: capitalizeAllWords(`${firstName} ${lastName}`.trim()),
            hasBicycles: Object.keys(selectedBikes).length > 0,
            bookingId: result.bookingId,
            pendingBookingsPath: result.pendingBookingsPath,
            cashPayment: true,
          },
        });
      } catch (error) {
        console.error("❌ Cash booking error:", error);
        alert(`Cash booking failed: ${error.message}`);
        setManualBookingProcessing(false);
      }
      return;
    }

    if (manualBookingMode === "bypassWaiver") {
      if (!window.confirm("Confirm Bypass Waiver? This will skip the waiver and go directly to payment.")) {
        return;
      }
      
      try {
        setManualBookingProcessing(true);
        // Create booking data
        const bookingDataResult = await createBookingData();
        if (!bookingDataResult) {
          setManualBookingProcessing(false);
          return;
        }
        
        // Navigate directly to Stripe Checkout
        navigate("/stripe-checkout", {
          state: {
            paymentUrl: bookingDataResult.paymentUrl,
            bookingId: bookingDataResult.bookingId,
          },
        });
      } catch (error) {
        console.error("❌ Bypass Waiver error:", error);
        alert(`Bypass Waiver Failed: ${error.message}`);
        setManualBookingProcessing(false);
      }
      return;
    }

    // Direct Mode: Save directly to DB
    if (!window.confirm("Confirm Manual Booking (No Payment)? This will create a confirmed booking without payment.")) {
      return;
    }

    try {
      setManualBookingProcessing(true);
      
      // 1. Create booking data (creates in pendingBookings)
      const bookingDataResult = await createBookingData();
      if (!bookingDataResult) {
        setManualBookingProcessing(false);
        return;
      }

      const { bookingId } = bookingDataResult;
      console.log(`🚧 MANUAL BOOKING: Created pending ${bookingId}`);

      // 2. Fetch the pending booking to get full data
      const tenantId = getTenantId();
      const storeId = getStoreId();
      let pendingPath = "pendingBookings";
      if (tenantId && storeId) {
        pendingPath = `tenants/${tenantId}/stores/${storeId}/pendingBookings`;
      }
      
      const pendingRef = doc(db, pendingPath, bookingId);
      const pendingSnap = await getDoc(pendingRef);
      
      if (!pendingSnap.exists()) {
        throw new Error("Failed to retrieve pending booking data");
      }
      
      const bookingData = pendingSnap.data();
      
      // 3. Prepare final booking data
      const finalBookingData = {
        ...bookingData,
        status: "confirmed",
        payments: [], // No payment taken
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      // Remove temp fields
      delete finalBookingData.tempCostBreakdown;

      // 4. Save to bookings collection
      let bookingsPath = "bookings";
      if (tenantId && storeId) {
        bookingsPath = `tenants/${tenantId}/stores/${storeId}/bookings`;
      }
      
      await setDoc(doc(db, bookingsPath, bookingId), finalBookingData);
      console.log(`✅ MANUAL BOOKING: Saved to ${bookingsPath}`);

      // 5. Delete from pending
      await deleteDoc(pendingRef);
      
      // 6. Send email and SMS notifications
      // Add a small delay to ensure Firestore write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        console.log(`📧 Sending notifications for booking ${bookingId} (tenant: ${tenantId}, store: ${storeId})`);
        const sendNotifications = httpsCallable(functions, "sendManualBookingNotifications");
        const result = await sendNotifications({
          bookingId,
          tenantId: tenantId || null,
          storeId: storeId || null,
        });
        console.log(`✅ Notifications sent successfully:`, result.data);
      } catch (notificationError) {
        console.error("❌ Failed to send notifications:", notificationError);
        console.error("❌ Error code:", notificationError.code);
        console.error("❌ Error message:", notificationError.message);
        console.error("❌ Error details:", notificationError.details);
        
        // Extract error message properly from Firebase error structure
        const errorMsg = notificationError.details?.message || notificationError.message || notificationError.code || "Unknown error";
        // Don't fail the entire operation if notifications fail - just log it
        console.warn(`⚠️ Booking created but notifications failed: ${errorMsg}`);
      }
      
      // Navigate directly to View Booking (no success alert)
      navigate(`/view-booking/${bookingId}`);
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error("❌ MANUAL BOOKING error:", error);
      alert(`Manual Booking Failed: ${error.message}`);
    } finally {
      setManualBookingProcessing(false);
    }
  };

  // Submit booking (standard flow: waiver then payment). If waiver already signed in store and valid, skip waiver page.
  const handleWaiverFlow = async () => {
    const hasBikesSelected = Object.values(selectedBikes).some((qty) => qty > 0);

    setShowCheckingWaiverModal(true);
    try {
      const template = await loadWaiverTemplate();
      const store = useNewBookingStore.getState();
      const canSkipWaiver =
        !!(
          store.waiverSigned &&
          store.waiverData &&
          template &&
          validateStoredWaiver(store.waiverData, template)
        );

      setShowCheckingWaiverModal(false);

      if (canSkipWaiver) {
        setSubmitting(true);
        try {
          // Must save: the waiver page (which normally writes the pending booking)
          // is skipped, so without this the customer pays for a booking the webhook
          // can never find. createBookingData embeds the stored waiver.
          const bookingResult = await createBookingData(true);
          if (!bookingResult) return;
          navigate("/stripe-checkout", {
            state: {
              paymentUrl: bookingResult.paymentUrl,
              bookingId: bookingResult.bookingId,
              referrer: "/rental-bookings",
            },
          });
        } finally {
          setSubmitting(false);
        }
        return;
      }
    } catch (err) {
      console.error("❌ Error checking waiver:", err);
      setShowCheckingWaiverModal(false);
    }

    // Normal flow: go to waiver page
    try {
      setSubmitting(true);
      const bookingResult = await createBookingData(false);
      if (!bookingResult) return;

      let bookingDataForState = null;
      if (bookingResult.bookingData) {
        bookingDataForState = {
          ...bookingResult.bookingData,
          createdAt: bookingResult.bookingData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          startDate: bookingResult.bookingData.startDate?.toDate?.()?.toISOString() || new Date().toISOString(),
          endDate: bookingResult.bookingData.endDate?.toDate?.()?.toISOString() || new Date().toISOString(),
          createdBy: bookingResult.bookingData.createdBy ? {
            ...bookingResult.bookingData.createdBy,
            timestamp: bookingResult.bookingData.createdBy.timestamp?.toDate?.()?.toISOString() || bookingResult.bookingData.createdBy.timestamp,
          } : bookingResult.bookingData.createdBy,
        };
      }

      navigate("/waiver", {
        state: {
          customerName: `${firstName} ${lastName}`.trim(),
          hasBicycles: hasBikesSelected,
          paymentUrl: bookingResult.paymentUrl,
          bookingId: bookingResult.bookingId,
          bookingData: bookingDataForState,
          pendingBookingsPath: bookingResult.pendingBookingsPath,
        },
      });
    } catch (error) {
      console.error("❌ Error in waiver flow:", error);
      alert("Error creating booking: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // `cashPayment` marks the booking as awaiting cash and skips Stripe entirely. A
  // payment link must NOT be minted for cash: it would be an orphan that anyone
  // holding the URL could still pay, and the webhook would then confirm the booking
  // as card-paid behind the register's back.
  const createBookingData = async (saveToFirestore = true, cashPayment = false) => {
    if (!startDate || !endDate) {
      alert("Please select dates");
      return null;
    }
    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(endDate);
    endDay.setHours(0, 0, 0, 0);
    if (startDay.getTime() === endDay.getTime()) {
      alert("Minimum rental is 1 full day. Please select two different dates (start and end) on the calendar.");
      return null;
    }

    const selectedEntries = Object.entries(selectedBikes).filter(
      ([_, qty]) => qty > 0
    );

    // Only require address if transport is needed
    const needsAddress = deliveryNeeded || homePickupNeeded;
    if (
      !firstName ||
      !lastName ||
      !customerEmail ||
      !customerPhone ||
      (needsAddress && !addressInput.trim())
    ) {
      alert("Please fill in all required customer information fields");
      return null;
    }

    try {

      // Calculate days — midnight-to-midnight, rounded; must match the quote calc
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 1;
      const numBikes = selectedEntries.reduce((sum, [_, qty]) => sum + qty, 0);

      const bicycles = selectedEntries.map(([name, quantity]) => {
        // Find the product in inventory to get its ID
        const product = inventory.find((b) => b.name === name);
        return {
          id: product?.id || product?.productId || name, // Use id, productId, or fallback to name
          name,
          quantity,
          // What was actually charged, not the catalog price -- receipts and
          // edit-time recalculation must match the amount taken.
          pricePaid: hasRateOverride ? dailyRateOverrideCents : product?.price || 0,
        };
      });

      // Convert products2 from object to array format with name and option
      const products2Array = [];
      Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
        const addOn = addOns.find((a) => a.id === addOnId);
        if (addOn) {
          let quantity = 0;
          let pricePaid = addOn.price || 0;

          if (typeof value === "object" && value !== null) {
            quantity = value.quantity || 0;
          } else if (value === true) {
            quantity = 1;
          }
          
          if (addOn.option === "per-bike") {
             // For per-bike, store TOTAL price paid (unit price * num bikes)
             pricePaid = (addOn.price || 0) * numBikes;
          }
          
          if (quantity > 0) {
            products2Array.push({
              id: addOnId,
              name: addOn.name,
              quantity: quantity,
              option: addOn.option,
              pricePaid: pricePaid, // Store price paid in cents
            });
          }
        }
      });

      // Clean phone number (remove dashes) for consistent searching
      const cleanPhoneNumber = customerPhone.replace(/\D/g, "");

      // Lowercase names and email for consistent searching
      const firstNameLower = firstName.trim().toLowerCase();
      const lastNameLower = lastName.trim().toLowerCase();
      const emailLower = customerEmail.trim().toLowerCase();

      // Get authenticated user info if logged in
      const currentUser = auth.currentUser;
      const editedBy = currentUser
        ? {
            uid: currentUser.uid,
            email: currentUser.email,
            timestamp: new Date().toISOString(),
          }
        : null;

      // Use the same calculation as displayed on screen
      // Calculate from selections if rentalCost is not available or invalid
      let subtotal = 0;
      if (rentalCost && rentalCost.totalCost) {
        // rentalCost.totalCost is in DOLLARS string, convert to CENTS number
        subtotal = Math.round(Number(rentalCost.totalCost) * 100);
      } else {
        // Calculate from selections (same as button validation)
        if (startDate && endDate) {
          const fallbackStart = new Date(startDate);
          const fallbackEnd = new Date(endDate);
          fallbackStart.setHours(0, 0, 0, 0);
          fallbackEnd.setHours(0, 0, 0, 0);
          const totalDays =
            Math.round(
              (fallbackEnd - fallbackStart) / (1000 * 60 * 60 * 24)
            ) || 1;

          // Add bicycle costs in CENTS (prices are in cents)
          let totalBikes = 0;
          Object.entries(selectedBikes).forEach(([bikeName, quantity]) => {
            if (quantity > 0) {
              totalBikes += quantity;
              const bike = inventory.find((b) => b.name === bikeName);
              if (bike) {
                // effectiveBikePriceCents(bike) is in cents
                subtotal += effectiveBikePriceCents(bike) * quantity * totalDays;
              }
            }
          });

          // Add add-ons costs in CENTS (prices are in cents)
          Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
            const addOn = addOns.find((a) => a.id === addOnId);
            if (addOn) {
              // addOn.price is in cents
              if (addOn.option === "per-booking" && value?.quantity) {
                subtotal += addOn.price * value.quantity;
              } else if (addOn.option === "per-bike" && value === true) {
                // For per-bike, multiply by total number of product1 items
                subtotal += addOn.price * totalBikes;
              } else if (addOn.option === "user-selection" && value?.quantity) {
                // Billed per day, matching the engine/EditBooking/discount util
                subtotal += addOn.price * value.quantity * totalDays;
              }
            }
          });
        }
      }

      // subtotal is in CENTS, add platform fee (already in cents).
      // No fee on an empty order — mirrors the engine's hasOrder gate.
      const subtotalWithPlatformFeeCents = subtotal + (subtotal > 0 ? bookingPlatformFee : 0);
      // Calculate tax in CENTS
      const taxAmountCents = salesTaxPercent
        ? Math.round(subtotalWithPlatformFeeCents * (salesTaxPercent / 100))
        : 0;
      
      // Calculate card processing fee in cents (only if enabled for purchases)
      const cardProcessingFeeForValidationCents = cardProcessingFeeForPurchases && cardProcessingFeePercent > 0
        ? Math.round((subtotalWithPlatformFeeCents + taxAmountCents) * (cardProcessingFeePercent / 100))
        : 0;
      
      const finalTotalCents = subtotalWithPlatformFeeCents + taxAmountCents + cardProcessingFeeForValidationCents;
      
      // Convert to dollars for use in rest of function
      const subtotalWithPlatformFee = subtotalWithPlatformFeeCents / 100;
      const taxAmount = taxAmountCents / 100;
      const finalTotal = finalTotalCents / 100;

      // Validate total before proceeding
      if (!Number.isFinite(finalTotal) || finalTotal <= 2.0) {
        alert(
          "Minimum order total is $2.00. Please add more items."
        );
        setSubmitting(false);
        return;
      }

      // Generate booking ID
      let bookingId;
      let customerBookingID;
      let existingBooking = null;
      
      // Get tenant/store context for this booking
      const tenantId = getTenantId();
      const storeId = getStoreId();
      
      if (isEditMode && originalBookingId) {
        // Editing existing booking - use same ID and update in place
        bookingId = originalBookingId;
        
        // Load existing booking to preserve data
        const bookingsPath = getCollectionPath("bookings");
        let bookingDoc = await getDoc(doc(db, bookingsPath, originalBookingId));
        
        // Try pendingBookings if not in bookings
        if (!bookingDoc.exists()) {
          const pendingPath = getCollectionPath("pendingBookings");
          bookingDoc = await getDoc(doc(db, pendingPath, originalBookingId));
        }
        
        // Try multi-tenant path if not authenticated
        if (!bookingDoc.exists() && bookingsPath === "bookings") {
          bookingDoc = await getDoc(doc(db, "tenants/1234/stores/999/bookings", originalBookingId));
        }
        
        if (bookingDoc.exists()) {
          existingBooking = bookingDoc.data();
          customerBookingID = existingBooking.customerBookingID;
        } else {
          console.error("❌ Could not find original booking in database");
          throw new Error("Original booking not found");
        }
      } else {
        // New booking - create new ID
        bookingId = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        // Short customer-facing ID: last 5 characters of the long booking id
        customerBookingID = shortBookingIdFromLong(bookingId);
      }

      // Calculate cost breakdown from rentalCost - ALL VALUES ARE ALREADY IN CENTS
      // Read from top-level fields which are stored in CENTS (not deliveryCost which is in dollars)
      const productCostCents = rentalCost?.rentalCost?.gross || 0; // Already in CENTS
      const deliveryCostCents = rentalCost?.deliveryApplied || 0; // Already in CENTS (top-level field)
      const pickupCostCents = rentalCost?.pickupApplied || 0; // Already in CENTS (top-level field)
      const durationDiscountCents = rentalCost?.durationDiscountAmount || 0; // Already in CENTS
      const distanceDiscountCents = rentalCost?.discountAmount || 0; // Already in CENTS
      // Read discounted amounts (what customer actually paid)
      const deliveryWithDiscountCents = rentalCost?.deliveryWithDiscount || deliveryCostCents; // Already in CENTS
      const pickupWithDiscountCents = rentalCost?.pickupWithDiscount || pickupCostCents; // Already in CENTS
      
      // Add booking platform fee (already in cents). No fee on an empty order —
      // mirrors the engine's hasOrder gate.
      const hasOrderForFee = productCostCents + deliveryCostCents + pickupCostCents > 0;
      const subtotalBeforeTaxCents = productCostCents + deliveryCostCents + pickupCostCents - durationDiscountCents - distanceDiscountCents + (hasOrderForFee ? bookingPlatformFee : 0);
      const salesTaxChargedCents = salesTaxPercent > 0 
        ? Math.round(subtotalBeforeTaxCents * (salesTaxPercent / 100))
        : 0;
      
      // Calculate card processing fee in cents (only if enabled for purchases)
      const cardProcessingFeeCents = cardProcessingFeeForPurchases && cardProcessingFeePercent > 0
        ? Math.round((subtotalBeforeTaxCents + salesTaxChargedCents) * (cardProcessingFeePercent / 100))
        : 0;
      
      const totalChargedCents = subtotalBeforeTaxCents + salesTaxChargedCents + cardProcessingFeeCents;
      
      // Store ALL values in CENTS (do NOT convert to dollars)
      const productCost = productCostCents; // Store in CENTS
      const deliveryCost = deliveryCostCents; // Store in CENTS
      const pickupCost = pickupCostCents; // Store in CENTS
      const durationDiscount = durationDiscountCents; // Store in CENTS
      const distanceDiscount = distanceDiscountCents; // Store in CENTS
      const subtotalBeforeTax = subtotalBeforeTaxCents; // Store in CENTS
      const salesTaxCharged = salesTaxChargedCents; // Store in CENTS
      const totalCharged = totalChargedCents; // Store in CENTS
      
      console.log("🔍 DEBUG - Booking cost breakdown (ALL IN CENTS):", JSON.stringify({
        productCostCents,
        deliveryCostCents,
        pickupCostCents,
        durationDiscountCents,
        distanceDiscountCents,
        bookingPlatformFee,
        cardProcessingFeeCents,
        subtotalBeforeTaxCents,
        salesTaxChargedCents,
        totalChargedCents
      }, null, 2));

      // Get discount objects from rentalCost (new structure)
      const deliveryDiscountObj = rentalCost?.deliveryDiscountObj || {
        discountPercent: 0,
        discountName: '',
        discountCents: 0,
      };
      const durationDiscountObj = rentalCost?.durationDiscountObj || {
        discountPercent: 0,
        discountName: '',
        discountCents: 0,
      };

      // Calculate numMiles (1 decimal place max) from distanceMiles (calculated from starting location to delivery address)
      const numMiles = distanceMiles !== null && distanceMiles !== undefined 
        ? parseFloat(distanceMiles.toFixed(1)) 
        : null;

      // Generate 3-digit security PIN
      const generateSecurityPin = () => {
        return Math.floor(100 + Math.random() * 900).toString();
      };

      // Get selected user for metadata
      const selectedUser = useAuthStore.getState().selectedUser;
      let userMetadata = null;
      
      if (selectedUser) {
        // Authorized user - use their account info
        userMetadata = {
          userId: selectedUser.id,
          firstName: selectedUser.firstName || "",
          lastName: selectedUser.lastName || "",
          timestamp: Timestamp.now(),
        };
      } else {
        // Unauthorized user (customer) - use customer's name from form
        userMetadata = {
          userId: null, // No user ID for unauthorized users
          firstName: firstName.trim() || "",
          lastName: lastName.trim() || "",
          timestamp: Timestamp.now(),
        };
      }

      // Get waiver data from Zustand store
      const store = useNewBookingStore.getState();
      const storeWaiverData = store.waiverData;
      const storeWaiverSigned = store.waiverSigned;
      
      // Build waiverData array - include waiver if signed
      const waiverDataArray = [];
      if (storeWaiverSigned && storeWaiverData) {
        // Convert signedAt to Timestamp if it's a Date object
        const waiverEntry = {
          ...storeWaiverData,
          signedAt: storeWaiverData.signedAt instanceof Date 
            ? Timestamp.fromDate(storeWaiverData.signedAt)
            : (storeWaiverData.signedAt || Timestamp.now()),
        };
        waiverDataArray.push(waiverEntry);
      }

      // Build booking data - exact structure as specified
      const bookingData = {
        bookingId: bookingId,
        createdAt: existingBooking?.createdAt || Timestamp.now(),
        customerBookingID: customerBookingID,
        createdBy: userMetadata, // Add user metadata (always set, never null)
        customerEmail: emailLower,
        customerPhone: cleanPhoneNumber,
        deliveryAddress: (deliveryNeeded || homePickupNeeded) && addressInput.trim() ? addressInput.trim() : "",
        delivery: deliveryNeeded,
        homePickup: homePickupNeeded,
        startDate: Timestamp.fromDate(new Date(startDate)),
        endDate: Timestamp.fromDate(new Date(endDate)),
        failedPinAttempts: existingBooking?.failedPinAttempts || 0,
        adminNotes: adminNotes || existingBooking?.adminNotes || "",
        addressNotes: (() => {
          // Append gated community name to address notes if provided
          let finalAddressNotes = addressNotes || "";
          if (gatedCommunity && communityName.trim()) {
            const gatedCommunityNote = `Gated community: ${communityName.trim()}\n`;
            finalAddressNotes = finalAddressNotes.trim() 
              ? `${finalAddressNotes.trim()}\n${gatedCommunityNote}` 
              : gatedCommunityNote;
          }
          return finalAddressNotes;
        })(),
        firstName: firstNameLower,
        gatedCommunity: gatedCommunity,
        inStorePickup: inStorePickup,
        lastName: lastNameLower,
        numMiles: numMiles,
        payments: [],
        pinLocked: existingBooking?.pinLocked || false,
        securityPin: existingBooking?.securityPin || generateSecurityPin(),
        product1: bicycles,
        product2: products2Array,
        refunds: [],
        salesTaxPercent: salesTaxPercent,
        storeId: storeId || "999",
        tenantId: tenantId || "1234",
        waiverData: waiverDataArray,
        waiverSigned: storeWaiverSigned || false,
        bookingPlatformFee: bookingPlatformFee, // Store in cents
        productPriceAmount: productCostCents,
        cardProcessingFeeAmount: cardProcessingFeeCents,
        salesTaxAmount: salesTaxChargedCents,
        totalAmount: totalChargedCents,
        // Transportation costs at top level (store as 0 if not selected)
        deliveryCostCents: deliveryNeeded ? deliveryCostCents : 0, // Store in CENTS, 0 if not selected
        pickupCostCents: homePickupNeeded ? pickupCostCents : 0, // Store in CENTS, 0 if not selected
        // Discounted transportation costs (what customer actually paid)
        deliveryWithDiscount: deliveryNeeded ? deliveryWithDiscountCents : 0, // Store in CENTS, 0 if not selected
        pickupWithDiscount: homePickupNeeded ? pickupWithDiscountCents : 0, // Store in CENTS, 0 if not selected
        // Discount objects at top level (new structure)
        transportationDiscount: deliveryDiscountObj,
        durationDiscount: durationDiscountObj,
        tempCostBreakdown: {
          productCost: productCost, // Store in CENTS (no conversion)
          deliveryCost: deliveryCost, // Store in CENTS (no conversion)
          pickupCost: pickupCost, // Store in CENTS (no conversion)
          durationDiscount: durationDiscount, // Store in CENTS (no conversion)
          deliveryDiscount: distanceDiscount, // Store in CENTS (no conversion)
          salesTaxCharged: salesTaxCharged, // Store in CENTS (no conversion)
          totalCharged: totalCharged, // Store in CENTS (no conversion)
          // Store discounted amounts (what customer actually paid)
          deliveryWithDiscount: deliveryNeeded ? deliveryWithDiscountCents : 0, // Store in CENTS
          pickupWithDiscount: homePickupNeeded ? pickupWithDiscountCents : 0, // Store in CENTS
        },
      };

      // Determine pendingBookings path (same logic for both saveToFirestore true and false)
      // IMPORTANT:
      // Mark cash bookings so an abandoned one is identifiable in pendingBookings
      // rather than looking like a half-finished card checkout.
      if (cashPayment) {
        bookingData.paymentMethod = "cash";
        bookingData.status = "awaiting_cash";
      }

      // For authenticated staff (tenant/store context present), use multi-tenant pendingBookings path.
      // For unauthenticated public users (no tenant/store context), fall back to root 'pendingBookings'
      // to match existing Firestore rules and legacy data layout.
      const ctxTenantId = getTenantId();
      const ctxStoreId = getStoreId();
      let pendingBookingsPath;
      if (ctxTenantId && ctxStoreId) {
        pendingBookingsPath = getCollectionPath("pendingBookings");
      } else {
        pendingBookingsPath = "pendingBookings";
      }
      
      // Save to temporary collection (pendingBookings) - will be moved to bookings after payment
      // Only save if saveToFirestore is true (for bypassWaiver flow)
      if (saveToFirestore) {
        const tempBookingRef = doc(db, pendingBookingsPath, bookingId);
        await setDoc(tempBookingRef, bookingData);

        // Store booking in localStorage for payment success page
        try {
          const bookingDataForStorage = {
            ...bookingData,
            // Convert Timestamps to ISO strings for localStorage
            createdAt: bookingData.createdAt.toDate().toISOString(),
            startDate: bookingData.startDate.toDate().toISOString(),
            endDate: bookingData.endDate.toDate().toISOString(),
            // Convert payment timestamps safely
            payments: bookingData.payments.map(p => ({
              ...p,
              timestamp: p.timestamp?.toDate ? p.timestamp.toDate().toISOString() : p.timestamp,
            })),
            // Convert refund timestamps safely  
            refunds: bookingData.refunds.map(r => ({
              ...r,
              timestamp: r.timestamp?.toDate ? r.timestamp.toDate().toISOString() : r.timestamp,
            })),
          };
          localStorage.setItem(
            "latestBooking",
            JSON.stringify(bookingDataForStorage)
          );
          localStorage.setItem("latestBookingId", bookingId);
        } catch (storageError) {
          console.error(
            "⚠️ Failed to save booking to localStorage:",
            storageError
          );
          // Continue with payment even if localStorage fails
        }
      }

      // Cash: no card transaction, so no payment link. The cash desk screen
      // (/cash-payment/:bookingId) completes this booking after the waiver.
      if (cashPayment) {
        return {
          paymentUrl: null,
          bookingId: bookingId,
          sessionId: null,
          bookingData: saveToFirestore ? undefined : bookingData,
          pendingBookingsPath: pendingBookingsPath,
        };
      }

      // Create payment link using totalChargedCents (already in cents)
      const createPaymentLink = httpsCallable(functions, "createPaymentLink");
      // Use totalChargedCents directly - it's already in cents
      const totalAmountCents = totalChargedCents;
      
      console.log("🔍 DEBUG - Stripe payment amount:", JSON.stringify({
        totalChargedCents,
        totalCharged_dollars: centsToDollars(totalChargedCents),
        finalTotal_dollars: finalTotal,
        totalAmountCents
      }, null, 2));

      // Validate amount is positive integer
      if (!Number.isFinite(totalAmountCents) || totalAmountCents <= 0) {
        alert(
          `Invalid payment amount: $${finalTotal.toFixed(
            2
          )}. Please contact support.`
        );
        setSubmitting(false);
        return;
      }

      const paymentResult = await createPaymentLink({
        amountCents: totalAmountCents,
        description: `Bicycle Rental - ${days} day(s), ${numBikes} bike(s)${products2Array.length > 0 ? ` + ${products2Array.map(a => `${a.name} x${a.quantity}`).join(", ")}` : ""}`,
        customerEmail: emailLower, // Use lowercase email
        customerPhone: cleanPhoneNumber, // Use cleaned phone number
        customerName: `${firstNameLower} ${lastNameLower}`.trim(), // Use lowercase names
        saveCard: true,
        bookingId: bookingId,
        testMode: useTestStripe,
      });


      // Stripe session ID is stored in the payment object's stripe.sessionId field
      // No need to update the root booking object

      // Return booking data for caller to use
      return {
        paymentUrl: paymentResult.data.url,
        bookingId: bookingId,
        sessionId: paymentResult.data.sessionId,
        bookingData: saveToFirestore ? undefined : bookingData, // Return booking data if not saved yet
        pendingBookingsPath: pendingBookingsPath, // Always return path (for later save when saveToFirestore=false)
      };
      
    } catch (error) {
      console.error("❌ Error creating booking:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
        data: error.data,
      });
      throw error;
    }
  };

  const submitBooking = async () => {
    try {
      setSubmitting(true);
      const bookingData = await createBookingData();
      if (!bookingData) {
        setSubmitting(false);
        return;
      }

      // Navigate to Stripe checkout page
      navigate("/stripe-checkout", {
        state: {
          paymentUrl: bookingData.paymentUrl,
          bookingId: bookingData.bookingId,
        },
      });
    } catch (error) {
      alert("Error creating booking: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          {/* Store Logo (if available) */}
          {storeLogo && (
            <Image 
              source={{ uri: storeLogo }} 
              style={styles.loadingLogo}
              resizeMode="contain"
            />
          )}
          
          {/* Bike Icon */}
          <View style={styles.loadingIconContainer}>
            <Text style={styles.loadingIcon}>🚴</Text>
          </View>

          {/* Loading Text */}
          <Text style={styles.loadingTitle}>Bonita Bikes</Text>
          <Text style={styles.loadingSubtitle}>
            Loading booking options...
          </Text>

          {/* Loading Spinner */}
          <View style={styles.loadingBarContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
          </View>

          <Text style={styles.loadingHint}>
            Getting the best bikes ready for you
          </Text>
        </View>
      </View>
    );
  }

  return (
    <>
      {/* Invisible overlay to close tooltip when clicking outside */}
      {visibleTooltip && (
        <TouchableOpacity
          style={styles.tooltipOverlay}
          activeOpacity={1}
          onPress={() => setVisibleTooltip(null)}
        />
      )}

      <AuthHeader 
        title={appHeaderTitle} 
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
        showHelperBar={false}
      />

      {/* Edit Mode Banner - For both authenticated and customer edit mode */}
      {isEditMode && (
        <View style={styles.editModeBanner}>
          <Text style={styles.editModeBannerText}>
            📝 EDIT MODE: You are adding to an existing booking. You can only ADD items and services, not remove them.
          </Text>
        </View>
      )}

      {/* Admin Notes Field - Only for authenticated users */}
      {isAuthenticated && (
        <View style={isDesktop ? styles.desktopContentWrapper : null}>
          <View style={styles.adminNotesContainer}>
            <TouchableOpacity 
              style={styles.adminNotesHeader}
              onPress={() => setShowAdminNotes(!showAdminNotes)}
            >
              <Text style={styles.adminNotesLabel}>
                📋 Administrator Notes {isEditMode ? "(Master Booking)" : ""}
              </Text>
              <Text style={styles.adminNotesToggle}>
                {showAdminNotes ? "▼ Hide" : "▶ Show"}
              </Text>
            </TouchableOpacity>
            
            {showAdminNotes && (
              <>
                <TextInput
                  style={styles.adminNotesInput}
                  placeholder={isEditMode ? "Enter notes about this booking (visible across all versions)..." : "Enter notes about this new booking..."}
                  placeholderTextColor="#8A9794"
                  value={adminNotes}
                  onChangeText={setAdminNotes}
                  multiline
                  numberOfLines={3}
                />
                <Text style={styles.adminNotesHelper}>
                  {adminNotesSaving ? "Saving..." : "Notes auto-saved"}
                </Text>
              </>
            )}
          </View>
        </View>
      )}

          <ScrollView ref={scrollViewRef} style={styles.content}>
            <View style={isDesktop ? styles.desktopContentWrapper : null}>
        
        {/* Delivery Option Selection */}
        <View ref={deliveryOptionsRef} style={styles.section}>
          <Text style={styles.sectionTitle}>Pickup, Delivery, In-Store Pickup</Text>

          {/* In-Store Pickup Checkbox */}
          <TouchableOpacity
            style={styles.checkboxContainer}
            onPress={() => {
              // Cannot uncheck in-store pickup if neither delivery nor home pickup are checked
              if (inStorePickup && !deliveryNeeded && !homePickupNeeded) {
                return; // Disabled - cannot uncheck
              }
              
              // If both delivery and home pickup are selected, clicking in-store pickup will clear them
              if (!inStorePickup && deliveryNeeded && homePickupNeeded) {
                setDeliveryNeeded(false);
                setHomePickupNeeded(false);
                setInStorePickup(true);
                setGatedCommunity(false);
                setAddressNotes("");
                return;
              }
              
              const newValue = !inStorePickup;
              setInStorePickup(newValue);
            }}
            disabled={inStorePickup && !deliveryNeeded && !homePickupNeeded}
          >
            <View
              style={[
                styles.checkbox,
                inStorePickup && styles.checkboxChecked,
              ]}
            >
              {inStorePickup && <Text style={styles.checkboxCheckmark}>✓</Text>}
            </View>
            <Text style={styles.checkboxLabel}>{getInStorePickupLabel()}</Text>
          </TouchableOpacity>

          {/* Pickup Info Message - Show when in-store pickup is selected */}
          {shouldInStorePickupBeChecked() && pickupInfo && (
            <View
            style={[
                styles.infoMessageContainer,
                {
                  marginLeft: 0,
                  marginBottom: 12,
                  backgroundColor: "#E4F4EC",
                  borderLeftColor: "#1F8A5B",
              },
            ]}
          >
              <Text style={styles.infoMessageText}>
                {showPickupInfoFull 
                  ? pickupInfo 
                  : pickupInfo.split('\n').slice(0, 3).join('\n') + (pickupInfo.split('\n').length > 3 ? '...' : '')}
              </Text>
              {pickupInfo.split('\n').length > 3 && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  onPress={() => setShowPickupInfoFull(!showPickupInfoFull)}
                  style={styles.infoToggleButton}
                >
                  <Text style={styles.infoToggleText}>
                    {showPickupInfoFull ? '▼ Show Less' : '▶ Show More'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Delivery Needed Checkbox */}
          <TouchableOpacity
            style={styles.checkboxContainer}
            onPress={() => {
              // In edit mode, can only ADD delivery, not remove it
              if (isEditMode && originalBookingData?.delivery && deliveryNeeded) {
                return; // Prevent unchecking if already paid for
              }
              const newValue = !deliveryNeeded;
              setDeliveryNeeded(newValue);
              
              // Update in-store pickup based on new state
              if (newValue && homePickupNeeded) {
                // Both delivery and home pickup will be checked - uncheck in-store pickup
                setInStorePickup(false);
              } else {
                // Any other case - in-store pickup must be checked
                setInStorePickup(true);
              }
              
              // Don't clear address when deselecting - user may want to re-select later
              if (!newValue && !homePickupNeeded) {
                setGatedCommunity(false);
                setAddressNotes("");
              }
            }}
            disabled={isEditMode && originalBookingData?.delivery && deliveryNeeded}
          >
            <View
              style={[
                styles.checkbox,
                deliveryNeeded && styles.checkboxChecked,
              ]}
            >
              {deliveryNeeded && (
                <Text style={styles.checkboxCheckmark}>✓</Text>
              )}
            </View>
            <Text style={styles.checkboxLabel}>Delivery Needed</Text>
          </TouchableOpacity>

          {/* Home Pickup Needed Checkbox */}
          <TouchableOpacity
            style={styles.checkboxContainer}
            onPress={() => {
              // In edit mode, can only ADD home pickup, not remove it
              if (isEditMode && originalBookingData?.homePickup && homePickupNeeded) {
                return; // Prevent unchecking if already paid for
              }
              const newValue = !homePickupNeeded;
              setHomePickupNeeded(newValue);
              
              // Update in-store pickup based on new state
              if (newValue && deliveryNeeded) {
                // Both home pickup and delivery will be checked - uncheck in-store pickup
                setInStorePickup(false);
              } else {
                // Any other case - in-store pickup must be checked
                setInStorePickup(true);
              }
              
              // Don't clear address when deselecting - user may want to re-select later
              if (!newValue && !deliveryNeeded) {
                setGatedCommunity(false);
                setAddressNotes("");
              }
            }}
            disabled={isEditMode && originalBookingData?.homePickup && homePickupNeeded}
          >
            <View
              style={[
                styles.checkbox,
                homePickupNeeded && styles.checkboxChecked,
              ]}
            >
              {homePickupNeeded && (
                <Text style={styles.checkboxCheckmark}>✓</Text>
              )}
            </View>
            <Text style={styles.checkboxLabel}>Home Pickup Needed</Text>
          </TouchableOpacity>

          {/* Address Input with Google Maps Autocomplete - Only show if Delivery or Home Pickup Needed */}
          {(deliveryNeeded || homePickupNeeded) && (
            <View
              ref={addressSectionRef}
              style={[
                styles.addressInputContainer,
                styles.addressSection,
                styles.addressDropdownWrapper,
              ]}
            >
              <Text style={styles.label}>
                Delivery Address <Text style={styles.requiredAsterisk}>*</Text>
              </Text>
              {isEditMode ? (
                <TextInput
                  style={[styles.addressInput, styles.inputDisabled]}
                  value={addressInput}
                  editable={false}
                  placeholder="Delivery Address"
                  placeholderTextColor="#AEB8B6"
                />
              ) : (
                <View style={styles.addressInputRow}>
                  <View style={styles.addressInputWrapper}>
                    <GoogleAddressInput
                      value={addressInput}
                      onChangeText={(text) => {
                        setAddressInput(text);
                        // Clear confirmation when address changes
                        if (addressConfirmed) {
                          setAddressConfirmed(false);
                        }
                      }}
                      onAddressSelected={(selected) => {
                        setAddressInput(selected);
                        // Clear confirmation when address changes
                        if (addressConfirmed) {
                          setAddressConfirmed(false);
                        }
                      }}
                      placeholder="Delivery Address"
                      style={styles.googleAddressInput}
                      isValidated={addressInput.trim().length >= 7 ? (addressConfirmed ? true : false) : undefined}
                    />
                  </View>
                  <Animated.View
                    style={[
                      isValidateEnabled1 && !addressConfirmed && { opacity: validateButtonOpacity1 },
                    ]}
                  >
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.validateButton,
                        !isValidateEnabled1 && styles.validateButtonDisabled,
                        addressConfirmed && styles.validateButtonConfirmed,
                      ]}
                      disabled={!isValidateEnabled1}
                      onPress={() => handleValidateAddress(1)}
                    >
                      <Text style={[
                        styles.validateButtonText,
                        addressConfirmed && styles.validateButtonTextConfirmed,
                      ]}>
                        {addressConfirmed ? "✓ Validated" : "Validate"}
                      </Text>
                    </TouchableOpacity>
                  </Animated.View>
                </View>
              )}
            </View>
          )}

          {/* Delivery/Pickup Details - Only show if delivery or home pickup is selected */}
          {(deliveryNeeded || homePickupNeeded) && (
            <>
              <View style={{ marginTop: 16, marginBottom: 8 }}>
                <Text style={styles.sectionTitle}>
                  Delivery Rate ({formatDollarsAsUSD(centsToDollars(mileageRate))}/mile)
                </Text>
                <Text
                  style={[
                    styles.infoText,
                    {
                      textAlign: "left",
                      paddingTop: 4,
                      paddingBottom: 4,
                      color: "#5C6B68",
                    },
                  ]}
                >
                  Loading/Unloading Fee: {formatDollarsAsUSD(centsToDollars(loadingFee))}
                </Text>

                {/* Available Transportation Discounts */}
                {useDeliveryDiscounts && deliveryDiscounts.length > 0 && (
                  <View style={{ marginTop: 12, marginBottom: 8 }}>
                    <Text style={{ fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 10 }}>
                      Available Transportation Discounts:
                    </Text>
                    {(() => {
                      // Calculate current equipment subtotal in dollars for discount comparison
                      // Use rentalCost if available, otherwise calculate from current selections
                      let equipmentSubtotalDollars = 0;
                      if (rentalCost?.rentalCost?.gross) {
                        equipmentSubtotalDollars = centsToDollars(rentalCost.rentalCost.gross); // Convert from CENTS to dollars
                      } else {
                        // Calculate from current selections
                        let equipmentSubtotalCents = 0;
                        if (startDate && endDate) {
                          const totalDays = Math.round(
                            (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)
                          ) || 1;
                          Object.entries(selectedBikes).forEach(([bikeName, quantity]) => {
                            if (quantity > 0) {
                              const bike = inventory.find((b) => b.name === bikeName);
                              if (bike) {
                                equipmentSubtotalCents += effectiveBikePriceCents(bike) * quantity * totalDays;
                              }
                            }
                          });
                          Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
                            const addOn = addOns.find((a) => a.id === addOnId);
                            if (addOn) {
                              const totalBikes = Object.values(selectedBikes).reduce((sum, qty) => sum + qty, 0);
                              if (addOn.option === "per-booking" && value?.quantity) {
                                equipmentSubtotalCents += addOn.price * value.quantity;
                              } else if (addOn.option === "per-bike" && value === true) {
                                equipmentSubtotalCents += addOn.price * totalBikes;
                              } else if (addOn.option === "user-selection" && value?.quantity) {
                                equipmentSubtotalCents += addOn.price * value.quantity;
                              }
                            }
                          });
                        }
                        equipmentSubtotalDollars = centsToDollars(equipmentSubtotalCents);
                      }
                      
                      return [...deliveryDiscounts].sort((a, b) => (a.from || 0) - (b.from || 0)).map((discount, index) => {
                        // Discount tiers are stored in cents, convert to dollars for comparison
                        const discountFromDollars = centsToDollars(discount.from || 0);
                        const discountToDollars = centsToDollars(discount.to || 0);
                        const isInRange = equipmentSubtotalDollars >= discountFromDollars && equipmentSubtotalDollars <= discountToDollars;
                      
                        // Find all applicable discounts and determine if this is the highest
                        const applicableDiscounts = deliveryDiscounts.filter(d => {
                            const dFromDollars = centsToDollars(d.from || 0);
                            const dToDollars = centsToDollars(d.to || 0);
                            return equipmentSubtotalDollars >= dFromDollars && equipmentSubtotalDollars <= dToDollars;
                        });
                        const highestDiscount = applicableDiscounts.length > 0 
                          ? applicableDiscounts.reduce((max, d) => (d.value > max.value) ? d : max, applicableDiscounts[0])
                          : null;
                        const isActive = highestDiscount && discount.id === highestDiscount.id;
                        
                        // Calculate how much more needed to unlock this discount (in dollars)
                        const amountNeeded = equipmentSubtotalDollars < discountFromDollars 
                          ? discountFromDollars - equipmentSubtotalDollars 
                          : 0;
                        
                        return (
                          <View key={index} style={styles.discountTierRow}>
                            <Text style={[
                              styles.discountTierText,
                              isActive ? styles.discountTierActive : styles.discountTierInactive
                            ]}>
                              ${discountFromDollars}-${discountToDollars} order: {discount.value}% off delivery
                              {isActive && " ✓"}
                            </Text>
                            {amountNeeded > 0 && (
                              <Text style={styles.discountTierNeeded}>
                                ${amountNeeded.toFixed(2)} to unlock
                              </Text>
                            )}
                          </View>
                        );
                      });
                    })()}
                  </View>
                )}
              </View>

              <TouchableOpacity
                style={[
                  styles.checkboxContainer,
                  styles.gatedCommunityContainer,
                ]}
                onPress={() => {
                  const newValue = !gatedCommunity;
                  setGatedCommunity(newValue);
                  // Clear community name when unchecking
                  if (!newValue) {
                    setCommunityName("");
                  }
                }}
              >
                <View
                  style={[
                    styles.checkbox,
                    styles.gatedCommunityCheckbox,
                    gatedCommunity && styles.gatedCommunityChecked,
                  ]}
                >
                  {gatedCommunity && (
                    <Text style={styles.gatedCommunityCheckmark}>✓</Text>
                  )}
                </View>
                <Text style={styles.gatedCommunityLabel}>Gated Community</Text>
              </TouchableOpacity>

              {/* Community Name Input - Show when Gated Community is checked */}
              {gatedCommunity && (
                <View style={styles.communityNameContainer}>
                  <TextInput
                    style={styles.communityNameInput}
                    placeholder="Community name (required)..."
                    placeholderTextColor="#AEB8B6"
                    value={communityName}
                    onChangeText={(text) => setCommunityName(capitalizeAllWords(text))}
                  />
                </View>
              )}

              {/* Delivery Info Message */}
              {deliveryInfo && (
                <View style={styles.infoMessageContainer}>
                  <Text style={styles.infoMessageText}>
                    {showDeliveryInfoFull 
                      ? deliveryInfo 
                      : deliveryInfo.split('\n').slice(0, 3).join('\n') + (deliveryInfo.split('\n').length > 3 ? '...' : '')}
                  </Text>
                  {deliveryInfo.split('\n').length > 3 && (
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      onPress={() => setShowDeliveryInfoFull(!showDeliveryInfoFull)}
                      style={styles.infoToggleButton}
                    >
                      <Text style={styles.infoToggleText}>
                        {showDeliveryInfoFull ? '▼ Show Less' : '▶ Show More'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}

              {/* Address Notes Field */}
              <View style={styles.addressNotesContainer}>
                <Text style={styles.addressNotesLabel}>
                  Address Notes (optional):
                </Text>
                <TextInput
                  style={styles.addressNotesInput}
                  placeholder="E.g., Gate code, building number, special instructions..."
                  placeholderTextColor="#AEB8B6"
                  value={addressNotes}
                  onChangeText={(text) => setAddressNotes(capitalizeFirstWord(text))}
                  multiline
                  numberOfLines={2}
                />
              </View>
            </>
          )}
            </View>
        {/* Date Selection */}
        <View style={styles.section}>
          <Text style={styles.sectionTitleSmall}>Select Date Range</Text>

          {/* Calendar Widget */}
          <View style={styles.calendarContainer}>
            {/* Month Navigation */}
            <View style={styles.monthNavigation}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                onPress={goToPreviousMonth}
                style={styles.monthButton}
              >
                <Text style={styles.monthButtonText}>‹</Text>
              </TouchableOpacity>
              <Text style={styles.monthTitle}>
                {currentMonth.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </Text>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                onPress={goToNextMonth}
                style={styles.monthButton}
              >
                <Text style={styles.monthButtonText}>›</Text>
              </TouchableOpacity>
            </View>

            {/* Calendar Grid */}
            <View style={styles.calendarGrid}>
              {generateCalendarDays().map((date, index) => {
                if (!date) {
                  return <View key={`empty-${index}`} style={styles.dayCell} />;
                }

                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isToday = date.getTime() === today.getTime();
                const isStart =
                  startDate && date.getTime() === startDate.getTime();
                const isEnd = endDate && date.getTime() === endDate.getTime();
                const inRange = isInRange(date);
                const isPast = date < today;
                // Unauthenticated users cannot select today as start (earliest is tomorrow)
                const isTodayDisabledForUnauth = !isAuthenticated && isToday;
                const isDisabled = isPast || isTodayDisabledForUnauth;

                return (
                  <TouchableOpacity
                    key={date.getTime()}
                    style={[
                      styles.dayCell,
                      isDesktop && styles.dayCellDesktop,
                      isToday && styles.todayCell,
                      isStart && styles.startCell,
                      isEnd && styles.endCell,
                      inRange && !isStart && !isEnd && styles.rangeCell,
                      isPast && styles.pastCell,
                      isTodayDisabledForUnauth && styles.pastCell,
                    ]}
                    onPress={() => !isDisabled && handleDateSelect(date)}
                    disabled={isDisabled}
                  >
                    <Text style={styles.dayAbbreviationText}>
                      {getDayAbbreviation(date)}
                    </Text>
                    <Text
                      style={[
                        styles.dayText,
                        isToday && styles.todayText,
                        (isStart || isEnd) && styles.selectedText,
                        inRange && !isStart && !isEnd && styles.rangeText,
                        (isPast || isTodayDisabledForUnauth) && styles.pastText,
                      ]}
                    >
                      {date.getDate()}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            
            {/* Duration Discount Info (based on selected date range) */}
            {startDate && endDate && durationDiscountPreview && (
              <View style={styles.durationDiscountHint}>
                <Text style={styles.durationDiscountHintText}>
                  🎉 {durationDiscountPreview.discountName}
                </Text>
              </View>
            )}

            {/* Info Message - Show when dates are not selected */}
            {(!startDate || !endDate) && (
              <View style={styles.dateRequiredInfo}>
                <Text style={styles.dateRequiredInfoText}>
                  ⚠️ Minimum rental is 1 full day. Please select two different dates (start and end) on the calendar to continue (equipment can be returned before the end date if desired).
                </Text>
              </View>
            )}
            
            {/* Total Availability Display */}
            {startDate && endDate && availableBikes.length > 0 && (() => {
              const totalAvailable = availableBikes.reduce((sum, bike) => {
                const selected = selectedBikes[bike.name] || 0;
                return sum + (bike.available - selected);
              }, 0);
              return (
                <View style={styles.totalAvailabilityContainer}>
                  <Text style={styles.totalAvailabilityText}>
                    {totalAvailable} 🚴 available
                  </Text>
                </View>
              );
            })()}

            {/* Show/Hide Duration Discounts Toggle */}
            {useDurationDiscounts && durationDiscounts.length > 0 && (
              <TouchableOpacity
                style={styles.durationDiscountsToggle}
                onPress={() => setShowDurationDiscountsInCalendar(!showDurationDiscountsInCalendar)}
              >
                <Text style={styles.durationDiscountsToggleText}>
                  {showDurationDiscountsInCalendar ? "▼ Hide" : "▶ Show"} Available Duration Discounts
                </Text>
              </TouchableOpacity>
            )}

            {/* Available Duration Discounts - Show when toggle is on */}
            {showDurationDiscountsInCalendar && useDurationDiscounts && durationDiscounts.length > 0 && (
              <View style={styles.durationDiscountsContainer}>
                <Text style={styles.durationDiscountsTitle}>
                  Available Duration Discounts:
                </Text>
                {(() => {
                  // Calculate days if dates are selected, otherwise use 0
                  const days = (startDate && endDate) 
                    ? Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) || 1
                    : 0;
                  
                  return [...durationDiscounts].sort((a, b) => (a.fromDays || 0) - (b.fromDays || 0)).map((discount, index) => {
                    const isInRange = days >= discount.fromDays && days <= discount.toDays;
                    
                    // Find all applicable discounts and determine if this is the highest (only if dates are selected)
                    let isActive = false;
                    if (startDate && endDate) {
                      const applicableDiscounts = durationDiscounts.filter(d => {
                        return days >= d.fromDays && days <= d.toDays;
                      });
                      const highestDiscount = applicableDiscounts.length > 0 
                        ? applicableDiscounts.reduce((max, d) => (d.value > max.value) ? d : max, applicableDiscounts[0])
                        : null;
                      isActive = highestDiscount && discount.id === highestDiscount.id;
                    }
                    
                    // Calculate how many more days needed to unlock this discount
                    const daysNeeded = days < discount.fromDays 
                      ? discount.fromDays - days 
                      : 0;
                    
                    return (
                      <View key={index} style={styles.discountTierRow}>
                        <Text style={[
                          styles.discountTierText,
                          isActive ? styles.discountTierActive : styles.discountTierInactive
                        ]}>
                          {discount.fromDays}-{discount.toDays} days: {discount.value}% off product price
                          {isActive && " ✓"}
                        </Text>
                        {daysNeeded > 0 && (
                          <Text style={styles.discountTierNeeded}>
                            {daysNeeded} more day{daysNeeded !== 1 ? 's' : ''} to unlock
                          </Text>
                        )}
                      </View>
                    );
                  });
                })()}
              </View>
            )}
          </View>
        </View>
          {/* Staff only — a negotiated rate is not something a customer may set. */}
          {isAuthenticated && (
            <View style={[styles.section, { borderLeftWidth: 4, borderLeftColor: "#B4761B" }]}>
              <Text style={{ fontSize: 15, fontWeight: "700", color: "#7A4E12", marginBottom: 4 }}>
                Daily rate override
              </Text>
              <Text style={{ fontSize: 12, color: "#5C6B68", lineHeight: 17, marginBottom: 10 }}>
                Optional. Charges every {product1Title.toLowerCase()} at this rate per day
                instead of its listed price. Leave blank to use normal pricing.
              </Text>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: "#5C6B68", marginRight: 6 }}>$</Text>
                <TextInput
                  style={{
                    flex: 1,
                    borderWidth: 1,
                    borderColor: "#DDE3E2",
                    borderRadius: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    fontSize: 16,
                    fontWeight: "600",
                    color: "#0F1A19",
                    backgroundColor: "#FFFFFF",
                  }}
                  value={hasRateOverride ? (dailyRateOverrideCents / 100).toFixed(2) : ""}
                  onChangeText={(text) => {
                    const cleaned = text.replace(/[^0-9.]/g, "");
                    const dollars = parseFloat(cleaned);
                    updateBookingData({
                      dailyRateOverrideCents:
                        cleaned && Number.isFinite(dollars) ? Math.round(dollars * 100) : null,
                    });
                  }}
                  placeholder="Listed price"
                  placeholderTextColor="#AEB8B6"
                  keyboardType="decimal-pad"
                  inputMode="decimal"
                />
                <Text style={{ fontSize: 13, color: "#5C6B68", marginLeft: 8 }}>/ day each</Text>
              </View>
              {hasRateOverride && (
                <Text style={{ fontSize: 12, color: "#7A4E12", marginTop: 8, fontWeight: "600" }}>
                  Override active — duration discounts do not apply on top of a negotiated rate.
                </Text>
              )}
            </View>
          )}

        {/* Available Bikes */}
        {startDate && endDate && availableBikes.length > 0 && (
          <View ref={bicyclesSectionRef} style={styles.section}>
            <Text style={styles.productSectionTitle}>{product1Title}</Text>
            {availableBikes.map((bike, index) => (
              <View key={index} style={styles.bikeCard}>
                {/* Name/Title with info icon */}
                <View style={styles.bikeNameRow}>
                  <Text style={styles.bikeName}>{bike.name}</Text>
                  {bike.description && (
                    <TouchableOpacity
                      style={styles.infoIcon}
                      onPress={() => {
                        setVisibleTooltip(
                          visibleTooltip === bike.name ? null : bike.name
                        );
                      }}
                    >
                      <Text style={styles.infoIconText}>ℹ️</Text>
                    </TouchableOpacity>
                  )}
                </View>
                {/* Tooltip */}
                {visibleTooltip === bike.name && (
                  <View style={styles.tooltip}>
                    <Text style={styles.tooltipText}>
                      {bike.description ||
                        "Additional information about this bicycle will appear here."}
                    </Text>
                    <View style={styles.tooltipArrow} />
                  </View>
                )}
                
                {/* Included Items */}
                {bike.includedItems && (
                  <Text style={styles.includedItemsText}>
                    Includes: {bike.includedItems}
                  </Text>
                )}

                {/* Image Container - 100% width */}
                <View style={styles.bikeImageContainer}>
                  {bike.imageUrl ? (
                    <TouchableOpacity
                      onPress={() => {
                        setSelectedImageUrl(bike.imageUrl);
                        setImageModalVisible(true);
                      }}
                    >
                      <img
                        src={bike.imageUrl}
                        alt={bike.name}
                        style={{
                          ...styles.productImage,
                          ...(isDesktop ? styles.productImageDesktop : {})
                        }}
                      />
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.bikeImagePlaceholder}>
                      <Text style={styles.bikeImagePlaceholderText}>📷</Text>
                    </View>
                  )}
                </View>

                {/* Price, Availability and Controls Container - 30%/40%/30% layout */}
                <View style={styles.bikeInfoControlsContainer}>
                  {/* Left Container (30%): Price and Duration Discount */}
                  <View style={{ width: "30%", justifyContent: "center" }}>
                    {(() => {
                      const durationDiscountPercent =
                        (durationDiscountPreview?.discountPercent ??
                          rentalCost?.durationDiscountObj?.discountPercent) || 0;
                      const durationDiscountName =
                        durationDiscountPreview?.discountName ||
                        rentalCost?.durationDiscountObj?.discountName ||
                        "";
                      const hasDiscount =
                        durationDiscountPercent > 0 && durationDiscountName;

                      const regularDailyPrice = formatDollarsAsUSD(
                        centsToDollars(effectiveBikePriceCents(bike))
                      );

                      if (!hasDiscount) {
                        return (
                          <>
                            <Text style={styles.bikePrice}>
                              {regularDailyPrice}/day
                            </Text>
                            <Text
                              style={{
                                fontSize: 11,
                                fontStyle: "italic",
                                color: "#8A9794",
                                marginTop: 2,
                              }}
                            >
                              (No duration discount unlocked)
                            </Text>
                          </>
                        );
                      }

                      const discountedDailyPrice = formatDollarsAsUSD(
                        centsToDollars(
                          Math.round(
                            effectiveBikePriceCents(bike) * (1 - durationDiscountPercent / 100)
                          )
                        )
                      );

                      return (
                        <>
                          <Text
                            style={[
                              styles.bikePrice,
                              { textDecorationLine: "line-through", color: "#8A9794" },
                            ]}
                          >
                            {regularDailyPrice}/day
                          </Text>
                          <Text
                            style={[
                              styles.bikePrice,
                              { color: "#1F8A5B", marginTop: 2 },
                            ]}
                          >
                            {discountedDailyPrice}/day
                          </Text>
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "600",
                              color: "#1F8A5B",
                              marginTop: 2,
                            }}
                          >
                            🎉 {durationDiscountName}
                          </Text>
                        </>
                      );
                    })()}
                  </View>

                  {/* Middle Container (40%): Quantity Controls */}
                  <View style={{ width: '40%', justifyContent: 'center', alignItems: 'center' }}>
                    {bike.available > 0 ? (
                      <View style={styles.quantitySelector}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.qtyButton}
                          onPress={() => {
                            const current = selectedBikes[bike.name] || 0;
                            if (current > 0) {
                              handleBikeSelect(bike.name, current - 1);
                            }
                          }}
                          disabled={
                            (selectedBikes[bike.name] || 0) === 0 ||
                            (isEditMode && (selectedBikes[bike.name] || 0) <= (originalBicycleQuantities[bike.name] || 0))
                          }
                        >
                          <Text style={styles.qtyButtonText}>-</Text>
                        </TouchableOpacity>
                        <Text style={styles.qtyValue}>
                          {selectedBikes[bike.name] || 0}
                        </Text>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.qtyButton}
                          onPress={() => {
                            const current = selectedBikes[bike.name] || 0;
                            const maxAvailable = bike.available;
                            if (current < maxAvailable) {
                              handleBikeSelect(bike.name, current + 1);
                            }
                          }}
                          disabled={
                            (selectedBikes[bike.name] || 0) >= bike.available
                          }
                        >
                          <Text style={styles.qtyButtonText}>+</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <Text style={styles.unavailableText}>Unavailable</Text>
                    )}
                  </View>

                  {/* Right Container (30%): Availability and Line Total */}
                  <View
                    style={{
                      width: "30%",
                      justifyContent: "center",
                      alignItems: "flex-end",
                    }}
                  >
                    <AvailabilityText style={styles.bikeAvailability}>
                      {bike.available - (selectedBikes[bike.name] || 0)} Available!
                    </AvailabilityText>
                    {(() => {
                      // Show grand total for the entire booking (same as summary total)
                      if (!rentalCost) return null;

                      // Equipment cost in CENTS (product1 + product2 before discounts)
                      const equipmentCostCents =
                        rentalCost.rentalCost?.gross || 0;

                      // Delivery/pickup and discounts in CENTS
                      const deliveryCostCents = rentalCost.deliveryApplied || 0;
                      const pickupCostCents = rentalCost.pickupApplied || 0;
                      const transportationDiscountCents =
                        rentalCost.discountAmount || 0;
                      const durationDiscountCents =
                        rentalCost.durationDiscountAmount || 0;

                      // Subtotal in CENTS = equipment + delivery + pickup - discounts
                      const subtotalCents =
                        equipmentCostCents +
                        deliveryCostCents +
                        pickupCostCents -
                        transportationDiscountCents -
                        durationDiscountCents;

                      // Add booking platform fee (already in cents)
                      const subtotalWithPlatformFeeCents =
                        subtotalCents + bookingPlatformFee;

                      // Tax in CENTS
                      const taxAmountCents = salesTaxPercent
                        ? Math.round(
                            subtotalWithPlatformFeeCents *
                              (salesTaxPercent / 100)
                          )
                        : 0;

                      // Card processing fee in CENTS (only for purchases)
                      const cardProcessingFeeCents =
                        cardProcessingFeeForPurchases &&
                        cardProcessingFeePercent > 0
                          ? Math.round(
                              (subtotalWithPlatformFeeCents + taxAmountCents) *
                                (cardProcessingFeePercent / 100)
                            )
                          : 0;

                      const finalTotalCents =
                        subtotalWithPlatformFeeCents +
                        taxAmountCents +
                        cardProcessingFeeCents;

                      const finalTotalDollars =
                        centsToDollars(finalTotalCents);

                      return (
                        <Text
                          style={{
                            marginTop: 4,
                            fontSize: 13,
                            fontWeight: "600",
                            color: "#2E3A38",
                          }}
                        >
                          Total:{" "}
                          {formatDollarsAsUSDWithCents(finalTotalDollars)}
                        </Text>
                      );
                    })()}
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
        {/* Add-Ons Selection */}
        {startDate && endDate && availableAddOns.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.productSectionTitle}>{product2Title}</Text>
            {availableAddOns.map((addOn) => {
              const isPerBooking = addOn.option === "per-booking";
              const isUserSelection = addOn.option === "user-selection";
              const isPerBike = addOn.option === "per-bike";
              const isSelected =
                isPerBike
                  ? selectedAddOns[addOn.id] === true
                  : (selectedAddOns[addOn.id]?.quantity || 0) > 0;
              const quantity = isUserSelection
                ? selectedAddOns[addOn.id]?.quantity || 0
                : 0;

              // Calculate total bikes for per-bike display
              const totalBikes = Object.values(selectedBikes).reduce(
                (sum, qty) => sum + qty,
                0
              );

              return (
                <View key={addOn.id} style={styles.bikeCard}>
                  {/* Name/Title with info icon */}
                  <View style={styles.bikeNameRow}>
                    <Text style={styles.bikeName}>{addOn.name}</Text>
                    {addOn.description && (
                      <TouchableOpacity
                        style={styles.infoIcon}
                        onPress={() => {
                          setVisibleTooltip(
                            visibleTooltip === addOn.id ? null : addOn.id
                          );
                        }}
                      >
                        <Text style={styles.infoIconText}>ℹ️</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  {/* Tooltip */}
                  {visibleTooltip === addOn.id && (
                    <View style={styles.tooltip}>
                      <Text style={styles.tooltipText}>
                        {addOn.description ||
                          "Additional information about this add-on will appear here."}
                      </Text>
                      <View style={styles.tooltipArrow} />
                    </View>
                  )}
                  
                  {/* Included Items */}
                  {addOn.includedItems && (
                    <Text style={styles.includedItemsText}>
                      Includes: {addOn.includedItems}
                    </Text>
                  )}

                  {/* Image Container - 100% width */}
                  <View style={styles.bikeImageContainer}>
                    {addOn.imageUrl ? (
                      <TouchableOpacity
                        onPress={() => {
                          setSelectedImageUrl(addOn.imageUrl);
                          setImageModalVisible(true);
                        }}
                      >
                        <img
                          src={addOn.imageUrl}
                          alt={addOn.name}
                          style={{
                            ...styles.productImage,
                            ...(isDesktop ? styles.productImageDesktop : {})
                          }}
                        />
                      </TouchableOpacity>
                    ) : (
                      <View style={styles.bikeImagePlaceholder}>
                        <Text style={styles.bikeImagePlaceholderText}>📷</Text>
                      </View>
                    )}
                  </View>

                  {/* Price, Availability and Controls Container - 30%/40%/30% layout */}
                  <View style={styles.bikeInfoControlsContainer}>
                    {/* Left Container (30%): Price and Duration Discount */}
                    <View style={{ width: '30%', justifyContent: 'center' }}>
                      <Text style={styles.bikePrice}>
                        {formatDollarsAsUSD(centsToDollars(addOn.price))}
                        {isPerBike ? "/bike" : isPerBooking ? "" : "/day"}
                      </Text>
                    </View>

                    {/* Middle Container (40%): Quantity Controls */}
                    <View style={{ width: '40%', justifyContent: 'center', alignItems: 'center' }}>
                      {isPerBike ? (
                      <TouchableOpacity
                        style={[
                          styles.checkbox,
                          isSelected && styles.checkboxChecked,
                        ]}
                        onPress={() => {
                          // In edit mode, can only ADD add-ons, not remove them
                          const originalProduct2Item = originalBookingData?.product2?.find(p => p.id === addOn.id);
                          if (isEditMode && originalProduct2Item && isSelected) {
                            return; // Prevent unchecking if already paid for
                          }
                          handleAddOnSelect(addOn.id, isSelected ? 0 : 1, true, false);
                        }}
                        disabled={
                          isEditMode && 
                          originalBookingData?.product2?.find(p => p.id === addOn.id) && 
                          isSelected
                        }
                      >
                        {isSelected && (
                          <Text style={styles.checkboxCheckmark}>✓</Text>
                        )}
                      </TouchableOpacity>
                      ) : (isPerBooking || isUserSelection) ? (
                      <View style={styles.quantitySelector}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.qtyButton}
                          onPress={() => {
                            const current = selectedAddOns[addOn.id]?.quantity || (isPerBooking && isSelected ? 1 : 0);
                            if (current > 0) {
                              handleAddOnSelect(addOn.id, current - 1, false, isPerBooking);
                            }
                          }}
                          disabled={
                              (isPerBooking && !isSelected) ||
                              (isUserSelection && (selectedAddOns[addOn.id]?.quantity || 0) === 0) ||
                              (isEditMode && isUserSelection &&
                              (selectedAddOns[addOn.id]?.quantity || 0) <= 
                              (originalBookingData?.product2?.find(p => p.id === addOn.id)?.quantity || 0))
                          }
                        >
                          <Text style={styles.qtyButtonText}>-</Text>
                        </TouchableOpacity>
                          <Text style={styles.qtyValue}>
                            {selectedAddOns[addOn.id]?.quantity || 0}
                          </Text>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.qtyButton}
                          onPress={() => {
                            const current = selectedAddOns[addOn.id]?.quantity || (isPerBooking && isSelected ? 1 : 0);
                            const maxAvailable = addOn.inventory !== null && addOn.inventory !== undefined
                              ? (addOn.available !== undefined ? addOn.available : addOn.inventory)
                              : 999; // No limit if inventory not set
                            
                            if (current < maxAvailable) {
                              handleAddOnSelect(addOn.id, current + 1, false, isPerBooking);
                            }
                          }}
                          disabled={
                              (isUserSelection && 
                            addOn.inventory !== null && 
                            addOn.inventory !== undefined && 
                                (selectedAddOns[addOn.id]?.quantity || 0) >= (addOn.available !== undefined ? addOn.available : addOn.inventory))
                          }
                        >
                          <Text style={styles.qtyButtonText}>+</Text>
                        </TouchableOpacity>
                      </View>
                      ) : (
                        addOn.inventory !== null && addOn.inventory !== undefined && addOn.inventory === 0 ? (
                          <Text style={styles.unavailableText}>Unavailable</Text>
                        ) : null
                      )}
                    </View>

                    {/* Right Container (30%): Availability */}
                    <View style={{ width: '30%', justifyContent: 'center', alignItems: 'flex-end' }}>
                      {(isUserSelection || isPerBooking) && addOn.inventory !== null && addOn.inventory !== undefined && (
                        <AvailabilityText style={styles.bikeAvailability}>
                          {(addOn.available !== undefined ? addOn.available : addOn.inventory) - (selectedAddOns[addOn.id]?.quantity || 0)} Available!
                        </AvailabilityText>
                      )}
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        )}
        {/* Order Summary */}
        <View ref={orderSummaryRef} style={styles.section}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <Text style={styles.sectionTitle}>Order Summary</Text>
            <TouchableOpacity 
              style={{ paddingHorizontal: 12, paddingVertical: 6, backgroundColor: "#1F8A5B", borderRadius: 5 }}
              onPress={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <Text style={{ color: "#FFFFFF", fontSize: 12, fontWeight: "600" }}>⬆ Back To Top</Text>
            </TouchableOpacity>
          </View>

          {/* Refund Information - Show if booking has been refunded */}
          {originalBookingData?.refunds && originalBookingData.refunds.length > 0 && (
            <View style={styles.refundInfoSection}>
              <Text style={styles.refundInfoTitle}>↩️ Refund Information</Text>
              {originalBookingData.refunds.map((refund, index) => {
                const cents = Number(refund.amount ?? refund.refundAmount ?? 0);
                return (
                  <View key={index} style={styles.refundInfoItem}>
                    <Text style={styles.refundInfoText}>
                      Version {refund.version || index + 1}: ${(cents / 100).toFixed(2)} ({refund.refundPercentage}%)
                    </Text>
                  </View>
                );
              })}
              <View style={styles.refundInfoTotal}>
                <Text style={styles.refundInfoTotalText}>
                  Total Refunded: ${((originalBookingData.refunds || []).reduce((s, r) => s + Number(r.amount ?? r.refundAmount ?? 0), 0) / 100).toFixed(2)}
                </Text>
              </View>
            </View>
          )}

          {/* Rental Dates */}
          <View style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: "#EDF1F0" }}>
            <Text style={styles.summaryLabel}>Rental Dates:</Text>
            {startDate && endDate ? (
              <Text style={{ fontSize: 14, marginTop: 4 }}>
                <Text style={{ color: "#1F8A5B", fontWeight: "600" }}>
                  {formatDate(startDate)}
                </Text>
                {' → '}
                <Text style={{ color: "#0E7C6B", fontWeight: "600" }}>
                  {formatDate(endDate)}
                </Text>
              </Text>
            ) : (
              <Text style={{ fontSize: 14, marginTop: 4, color: "#8A9794" }}>Not selected</Text>
            )}
          </View>

          {/* Available Duration Discounts - Show directly beneath Rental Dates */}
          {useDurationDiscounts && durationDiscounts.length > 0 && startDate && endDate && (
            <>
              <View style={{ marginTop: 8, marginBottom: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 10 }}>
                  Available Duration Discounts:
                </Text>
                {(() => {
                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 1;
                  
                  return [...durationDiscounts].sort((a, b) => (a.fromDays || 0) - (b.fromDays || 0)).map((discount, index) => {
                    const isInRange = days >= discount.fromDays && days <= discount.toDays;
                    
                    // Find all applicable discounts and determine if this is the highest
                    const applicableDiscounts = durationDiscounts.filter(d => {
                      return days >= d.fromDays && days <= d.toDays;
                    });
                    const highestDiscount = applicableDiscounts.length > 0 
                      ? applicableDiscounts.reduce((max, d) => (d.value > max.value) ? d : max, applicableDiscounts[0])
                      : null;
                    const isActive = highestDiscount && discount.id === highestDiscount.id;
                    
                    // Calculate how many more days needed to unlock this discount
                    const daysNeeded = days < discount.fromDays 
                      ? discount.fromDays - days 
                      : 0;
                    
                    return (
                      <View key={index} style={styles.discountTierRow}>
                        <Text style={[
                          styles.discountTierText,
                          isActive ? styles.discountTierActive : styles.discountTierInactive
                        ]}>
                          {discount.fromDays}-{discount.toDays} days: {discount.value}% off product price
                          {isActive && " ✓"}
                        </Text>
                        {daysNeeded > 0 && (
                          <Text style={styles.discountTierNeeded}>
                            {daysNeeded} more day{daysNeeded !== 1 ? 's' : ''} to unlock
                          </Text>
                        )}
                      </View>
                    );
                  });
                })()}
              </View>
            </>
          )}

          {/* Selected Bicycles */}
          {Object.keys(selectedBikes).length > 0 ? (
            <>
              <Text style={styles.summarySectionTitle}>Bicycles:</Text>
              {Object.entries(selectedBikes).map(([bikeName, quantity]) => {
                if (quantity === 0) return null;
                const bike = inventory.find((b) => b.name === bikeName);
                const bikePrice = bike ? effectiveBikePriceCents(bike) : 0; // cents per day

                const totalDays =
                  startDate && endDate
                    ? Math.round(
                        (new Date(endDate) - new Date(startDate)) /
                          (1000 * 60 * 60 * 24)
                      ) || 1
                    : 1;

                // Base total in CENTS (before duration discount)
                const bikeTotalCents = bikePrice * quantity * totalDays;

                // Apply duration discount percent if available (product1-only)
                const durationDiscountPercent =
                  rentalCost?.durationDiscountPercent || 0;
                const bikeTotalAfterDiscountCents =
                  durationDiscountPercent > 0
                    ? Math.round(
                        bikeTotalCents * (1 - durationDiscountPercent / 100)
                      )
                    : bikeTotalCents;

                // Calculate discounted daily rate (price per day after duration discount)
                const discountedDailyRateCents = durationDiscountPercent > 0
                  ? Math.round(bikePrice * (1 - durationDiscountPercent / 100))
                  : bikePrice;

                return (
                  <View key={bikeName} style={styles.summaryItemRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.summaryItemText}>
                        {quantity}x {bikeName}
                      </Text>
                      <Text style={{ color: '#0E7C6B', fontSize: 14, marginTop: 2 }}>
                        {formatDollarsAsUSD(centsToDollars(discountedDailyRateCents))}/day
                      </Text>
                    </View>
                    <Text style={styles.summaryItemPrice}>
                      {formatDollarsAsUSDWithCents(
                        centsToDollars(bikeTotalAfterDiscountCents)
                      )}
                    </Text>
                  </View>
                );
              })}
            </>
          ) : (
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Bicycles:</Text>
              <Text style={styles.summaryValue}>None selected</Text>
            </View>
          )}

          {/* Selected Add-Ons */}
          {addOns.length > 0 && Object.keys(selectedAddOns).length > 0 && (
            <>
              <Text style={styles.summarySectionTitle}>Add-Ons:</Text>
              {Object.entries(selectedAddOns).map(([addOnId, value]) => {
                const addOn = addOns.find((a) => a.id === addOnId);
                if (!addOn) return null;

                const isPerBooking = addOn.option === "per-booking";
                const isPerBike = addOn.option === "per-bike";
                const isSelected =
                  isPerBike
                    ? value === true
                    : (value?.quantity || 0) > 0;

                // Hoisted so it's always in scope regardless of which branch renders
                const totalBikes = Object.values(selectedBikes || {}).reduce((sum, qty) => sum + qty, 0);

                if (!isSelected) return null;

                if (isPerBooking) {
                  const quantity = value?.quantity || 0;
                  const addOnTotal = addOn.price * quantity;
                  return (
                    <View key={addOnId} style={styles.summaryItemRow}>
                      <Text style={styles.summaryItemText}>
                        {quantity}x {addOn.name}
                      </Text>
                      <Text style={styles.summaryItemPrice}>
                        {formatDollarsAsUSDWithCents(centsToDollars(addOnTotal))}
                      </Text>
                    </View>
                  );
                } else if (isPerBike) {
                  const addOnTotal = addOn.price * totalBikes;
                  return (
                    <View key={addOnId} style={styles.summaryItemRow}>
                      <Text style={styles.summaryItemText}>
                        {addOn.name} ({totalBikes} {product1Title.toLowerCase()})
                      </Text>
                      <Text style={styles.summaryItemPrice}>
                        {formatDollarsAsUSDWithCents(centsToDollars(addOnTotal))}
                      </Text>
                    </View>
                  );
                } else {
                  const quantity = value?.quantity || 0;
                  const addOnTotal = addOn.price * quantity;
                  return (
                    <View key={addOnId} style={styles.summaryItemRow}>
                      <Text style={styles.summaryItemText}>
                        {quantity}x {addOn.name}
                      </Text>
                      <Text style={styles.summaryItemPrice}>
                        {formatDollarsAsUSDWithCents(centsToDollars(addOnTotal))}
                      </Text>
                    </View>
                  );
                }
              })}
            </>
          )}

          {/* Equipment Subtotal */}
          {(() => {
            if (!startDate || !endDate) return null;

            const totalDays =
              Math.round(
                (new Date(endDate) - new Date(startDate)) /
                  (1000 * 60 * 60 * 24)
              ) || 1;

            // Product1 (bicycles) subtotal in CENTS
            let product1SubtotalCents = 0;
            Object.entries(selectedBikes).forEach(([bikeName, quantity]) => {
              if (quantity > 0) {
                const bike = inventory.find((b) => b.name === bikeName);
                if (bike) {
                  product1SubtotalCents += effectiveBikePriceCents(bike) * quantity * totalDays;
                }
              }
            });

            // Product2 (add-ons) subtotal in CENTS
            let product2SubtotalCents = 0;
            const totalBikes = Object.values(selectedBikes).reduce(
              (sum, qty) => sum + qty,
              0
            );
            Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
              const addOn = addOns.find((a) => a.id === addOnId);
              if (addOn) {
                if (addOn.option === "per-booking" && value?.quantity) {
                  product2SubtotalCents += addOn.price * value.quantity;
                } else if (addOn.option === "per-bike" && value === true) {
                  product2SubtotalCents += addOn.price * totalBikes;
                } else if (addOn.option === "user-selection" && value?.quantity) {
                  product2SubtotalCents += addOn.price * value.quantity;
                }
              }
            });

            if (product1SubtotalCents <= 0 && product2SubtotalCents <= 0) {
              return null;
            }

            const durationDiscountCents =
              rentalCost?.durationDiscountAmount || 0;
            const durationDiscountPercent =
              rentalCost?.durationDiscountPercent || 0;

            // Product1 subtotal after duration discount (cannot go below 0)
            const product1AfterDiscountCents = Math.max(
              0,
              product1SubtotalCents - durationDiscountCents
            );

            const product1Subtotal = centsToDollars(product1SubtotalCents);
            const product1AfterDiscount = centsToDollars(
              product1AfterDiscountCents
            );
            const product2Subtotal = centsToDollars(product2SubtotalCents);

            return (
              <>
                <View style={styles.summaryDivider} />
                {/* Product1 subtotal and duration discount */}
                {product1SubtotalCents > 0 && (
                  <>
                    <View style={styles.summaryItemRow}>
                      <Text
                        style={[
                          styles.summaryItemText,
                          { fontWeight: "600", fontSize: 15 },
                        ]}
                      >
                        {product1Title} Subtotal
                      </Text>
                      <Text
                        style={[
                          styles.summaryItemPrice,
                          { fontWeight: "600", fontSize: 15 },
                        ]}
                      >
                        {formatDollarsAsUSDWithCents(product1AfterDiscount)}
                      </Text>
                    </View>
                    {durationDiscountCents > 0 && (
                      <>
                        <View style={styles.summaryItemRow}>
                          <Text
                            style={[
                              styles.summaryItemText,
                              styles.discountText,
                            ]}
                          >
                            You saved ({durationDiscountPercent}%)
                          </Text>
                          <Text
                            style={[
                              styles.summaryItemPrice,
                              styles.discountText,
                            ]}
                          >
                            -
                            {formatDollarsAsUSDWithCents(
                              centsToDollars(durationDiscountCents)
                            )}
                          </Text>
                        </View>
                      </>
                    )}
                  </>
                )}

                {/* Product2 subtotal (not discounted) */}
                {product2SubtotalCents > 0 && (
                  <View style={styles.summaryItemRow}>
                    <Text
                      style={[
                        styles.summaryItemText,
                        { fontWeight: "600", fontSize: 15 },
                      ]}
                    >
                      {product2Title} Subtotal
                    </Text>
                    <Text
                      style={[
                        styles.summaryItemPrice,
                        { fontWeight: "600", fontSize: 15 },
                      ]}
                    >
                      {formatDollarsAsUSDWithCents(product2Subtotal)}
                    </Text>
                  </View>
                )}
              </>
            );
          })()}

          {/* Available Transportation Discounts - Show whenever delivery/pickup selected */}
          {useDeliveryDiscounts && deliveryDiscounts.length > 0 && (deliveryNeeded || homePickupNeeded) && (
            <>
              <View style={styles.summaryDivider} />
              <View style={{ marginTop: 8, marginBottom: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 10 }}>
                  Available Transportation Discounts:
                </Text>
                {(() => {
                  // Calculate current equipment subtotal in dollars for discount comparison
                  // Always calculate from current selections to ensure accuracy
                  let equipmentSubtotalCents = 0;
                  if (startDate && endDate) {
                    const totalDays = Math.round(
                      (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)
                    ) || 1;
                    Object.entries(selectedBikes).forEach(([bikeName, quantity]) => {
                      if (quantity > 0) {
                        const bike = inventory.find((b) => b.name === bikeName);
                        if (bike) {
                          equipmentSubtotalCents += effectiveBikePriceCents(bike) * quantity * totalDays;
                        }
                      }
                    });
                    Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
                      const addOn = addOns.find((a) => a.id === addOnId);
                      if (addOn) {
                        const totalBikes = Object.values(selectedBikes).reduce((sum, qty) => sum + qty, 0);
                        if (addOn.option === "per-booking" && value?.quantity) {
                          equipmentSubtotalCents += addOn.price * value.quantity;
                        } else if (addOn.option === "per-bike" && value === true) {
                          equipmentSubtotalCents += addOn.price * totalBikes;
                        } else if (addOn.option === "user-selection" && value?.quantity) {
                          equipmentSubtotalCents += addOn.price * value.quantity;
                        }
                      }
                    });
                  }
                  const equipmentSubtotalDollars = centsToDollars(equipmentSubtotalCents);
                  
                  return [...deliveryDiscounts].sort((a, b) => (a.from || 0) - (b.from || 0)).map((discount, index) => {
                    // Discount tiers are stored in cents, convert to dollars for comparison
                    const discountFromDollars = centsToDollars(discount.from || 0);
                    const discountToDollars = centsToDollars(discount.to || 0);
                    const isInRange = equipmentSubtotalDollars >= discountFromDollars && equipmentSubtotalDollars <= discountToDollars;
                  
                  // Find all applicable discounts and determine if this is the highest
                  const applicableDiscounts = deliveryDiscounts.filter(d => {
                      const dFromDollars = centsToDollars(d.from || 0);
                      const dToDollars = centsToDollars(d.to || 0);
                      return equipmentSubtotalDollars >= dFromDollars && equipmentSubtotalDollars <= dToDollars;
                  });
                  const highestDiscount = applicableDiscounts.length > 0 
                    ? applicableDiscounts.reduce((max, d) => (d.value > max.value) ? d : max, applicableDiscounts[0])
                    : null;
                  const isActive = highestDiscount && discount.id === highestDiscount.id;
                  
                    // Calculate how much more needed to unlock this discount (in dollars)
                    const amountNeeded = equipmentSubtotalDollars < discountFromDollars 
                      ? discountFromDollars - equipmentSubtotalDollars 
                    : 0;
                  
                  return (
                    <View key={index} style={styles.discountTierRow}>
                      <Text style={[
                        styles.discountTierText,
                        isActive ? styles.discountTierActive : styles.discountTierInactive
                      ]}>
                        ${discountFromDollars}-${discountToDollars} order: {discount.value}% off delivery
                        {isActive && " ✓"}
                      </Text>
                      {amountNeeded > 0 && (
                        <Text style={styles.discountTierNeeded}>
                          ${amountNeeded.toFixed(2)} to unlock
                        </Text>
                      )}
                    </View>
                  );
                })
                })()}
              </View>
            </>
          )}

          {/* Rental Cost Breakdown - Delivery & Pickup - After Transportation Discount */}
          {((rentalCost && (deliveryNeeded || homePickupNeeded)) || (deliveryNeeded && !addressConfirmed) || (homePickupNeeded && !addressConfirmed)) && (
            <>
              {(() => {
                // These values are stored in CENTS at rentalCost.deliveryApplied and rentalCost.pickupApplied
                // Convert to dollars for display
                // Read from top-level fields which are stored in CENTS (not deliveryCost which is in dollars)
                const deliveryValCents = rentalCost ? (Number(rentalCost.deliveryApplied) || 0) : 0;
                const pickupValCents = rentalCost ? (Number(rentalCost.pickupApplied) || 0) : 0;
                const deliveryVal = centsToDollars(deliveryValCents);
                const pickupVal = centsToDollars(pickupValCents);

                if (deliveryValCents > 0 || pickupValCents > 0 || (deliveryNeeded && !addressConfirmed) || (homePickupNeeded && !addressConfirmed)) {
                return (
                  <>
                      <View style={styles.summaryDivider} />
                      {(deliveryNeeded && (deliveryValCents > 0 || !addressConfirmed)) && (
                      <View style={styles.summaryItemRow}>
                        {deliveryNeeded && !addressConfirmed ? (
                          <Text style={[styles.summaryItemText, { color: "#C4453D", fontWeight: "600" }]}>
                            Address not validated, cannot proceed
                          </Text>
                        ) : deliveryValCents > 0 ? (
                          <>
                            <Text style={styles.summaryItemText}>
                              Delivery{rentalCost?.miles ? ` (${rentalCost.miles.toFixed(1)} mi)` : ''}
                            </Text>
                            <Text style={styles.summaryItemPrice}>
                                {formatDollarsAsUSDWithCents(deliveryVal)}
                            </Text>
                          </>
                        ) : null}
                      </View>
                    )}
                      {pickupValCents > 0 && (
                      <View style={styles.summaryItemRow}>
                        <Text style={styles.summaryItemText}>
                          Pickup{rentalCost?.miles ? ` (${rentalCost.miles.toFixed(1)} mi)` : ''}
                        </Text>
                        <Text style={styles.summaryItemPrice}>
                            {formatDollarsAsUSDWithCents(pickupVal)}
                        </Text>
                      </View>
                    )}
                      {rentalCost && (() => {
                        const transportationDiscountCents = rentalCost.discountAmount || 0;
                        const transportationDiscountPercent = rentalCost.discountPercent || 0;
                        const transportationDiscountAmount = centsToDollars(Number(transportationDiscountCents));
                        
                        if (transportationDiscountCents > 0 && (deliveryNeeded || homePickupNeeded)) {
                          return (
                            <View style={styles.summaryItemRow}>
                              <Text style={[styles.summaryItemText, styles.discountText]}>
                                Transportation Discount ({transportationDiscountPercent}%)
                              </Text>
                              <Text style={[styles.summaryItemPrice, styles.discountText]}>
                                -{formatDollarsAsUSDWithCents(transportationDiscountAmount)}
                              </Text>
                            </View>
                          );
                        }
                        return null;
                      })()}
                  </>
                );
                }
                return null;
              })()}
            </>
          )}

          {/* Platform Fee, Tax, and Total - Show when rentalCost is available */}
          {rentalCost && (
            <>
              <View style={styles.summaryDivider} />
              {(() => {
                // Calculate subtotal in CENTS (without tax)
                // Recalculate equipment cost in CENTS from selected items
                let equipmentCostCents = 0;
                if (startDate && endDate) {
                  const totalDays = Math.round(
                    (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)
                  ) || 1;
                  
                  // Calculate bicycle costs in CENTS
                  Object.entries(selectedBikes).forEach(([bikeName, quantity]) => {
                    if (quantity > 0) {
                      const bike = inventory.find((b) => b.name === bikeName);
                      if (bike) {
                        equipmentCostCents += effectiveBikePriceCents(bike) * quantity * totalDays;
                      }
                    }
                  });
                  
                  // Calculate add-on costs in CENTS
                  const totalBikes = Object.values(selectedBikes).reduce((sum, qty) => sum + qty, 0);
                  Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
                    const addOn = addOns.find((a) => a.id === addOnId);
                    if (addOn) {
                      if (addOn.option === "per-booking" && value?.quantity) {
                        equipmentCostCents += addOn.price * value.quantity;
                      } else if (addOn.option === "per-bike" && value === true) {
                        equipmentCostCents += addOn.price * totalBikes;
                      } else if (addOn.option === "user-selection" && value?.quantity) {
                        equipmentCostCents += addOn.price * value.quantity;
                      }
                    }
                  });
                }
                
                // Get delivery, pickup, and discounts from rentalCost (all in CENTS)
                const deliveryCostCents = rentalCost.deliveryApplied || 0;
                const pickupCostCents = rentalCost.pickupApplied || 0;
                const transportationDiscountCents = rentalCost.discountAmount || 0;
                const durationDiscountCents = rentalCost.durationDiscountAmount || 0;
                
                // Subtotal in CENTS = equipment + delivery + pickup - discounts
                const subtotalCents = equipmentCostCents + deliveryCostCents + pickupCostCents - transportationDiscountCents - durationDiscountCents;
                
                // Add booking platform fee (already in cents)
                const subtotalWithPlatformFeeCents = subtotalCents + bookingPlatformFee;
                
                // Calculate tax in CENTS
                let taxAmountCents = 0;
                if (salesTaxPercent) {
                  // Calculate tax on the subtotal with platform fee (in cents)
                  taxAmountCents = Math.round(subtotalWithPlatformFeeCents * (salesTaxPercent / 100));
                }

                // Calculate card processing fee in cents (only if enabled for purchases)
                const cardProcessingFeeCents = cardProcessingFeeForPurchases && cardProcessingFeePercent > 0
                  ? Math.round((subtotalWithPlatformFeeCents + taxAmountCents) * (cardProcessingFeePercent / 100))
                  : 0;

                const finalTotalCents = subtotalWithPlatformFeeCents + taxAmountCents + cardProcessingFeeCents;
                
                // Convert to dollars for display
                const platformFeeDollars = centsToDollars(bookingPlatformFee);
                const cardProcessingFeeDollars = centsToDollars(cardProcessingFeeCents);
                const subtotalWithPlatformFee = centsToDollars(subtotalWithPlatformFeeCents);
                const taxAmount = centsToDollars(taxAmountCents);
                const finalTotal = centsToDollars(finalTotalCents);

                // Get discount percents and convert discount amounts to dollars for display
                const transportationDiscountPercent = rentalCost.discountPercent || 0;
                // Convert discount amounts from CENTS to dollars for display
                const transportationDiscountAmount = centsToDollars(Number(transportationDiscountCents));

                return (
                  <>
                    {bookingPlatformFee > 0 && (
                      <View style={styles.summaryItemRow}>
                        <Text style={styles.summaryItemText}>
                          Platform Fee
                        </Text>
                        <Text style={styles.summaryItemPrice}>
                          {formatDollarsAsUSDWithCents(platformFeeDollars)}
                        </Text>
                      </View>
                    )}
                    {cardProcessingFeeCents > 0 && (
                      <View style={styles.summaryItemRow}>
                        <Text style={styles.summaryItemText}>
                          Card Processing Fee
                        </Text>
                        <Text style={styles.summaryItemPrice}>
                          {formatDollarsAsUSDWithCents(cardProcessingFeeDollars)}
                        </Text>
                      </View>
                    )}
                    {salesTaxPercent && taxAmount > 0 && (
                      <View style={styles.summaryItemRow}>
                        <Text style={styles.summaryItemText}>
                          Sales Tax ({salesTaxPercent}%)
                        </Text>
                        <Text style={styles.summaryItemPrice}>
                          {formatDollarsAsUSDWithCents(taxAmount)}
                        </Text>
                      </View>
                    )}
                    <View style={styles.summaryTotalRow}>
                      <Text style={styles.summaryTotalLabel}>Total:</Text>
                      <Text style={styles.summaryTotalValue}>
                        {formatDollarsAsUSDWithCents(finalTotal)}
                      </Text>
                    </View>
                  </>
                );
              })()}
            </>
          )}

          {/* Calculate and show current total even if rentalCost not available */}
          {!rentalCost && (
            <>
              {/* Calculate subtotal from bicycles and add-ons */}
              {(() => {
                let subtotalCents = 0;

                // Calculate bicycle costs in CENTS (prices are in cents)
                if (startDate && endDate) {
                  const totalDays =
                    Math.round(
                      (new Date(endDate) - new Date(startDate)) /
                        (1000 * 60 * 60 * 24)
                    ) || 1;
                  let totalBikes = 0;
                  Object.entries(selectedBikes).forEach(
                    ([bikeName, quantity]) => {
                      if (quantity > 0) {
                        totalBikes += quantity;
                        const bike = inventory.find((b) => b.name === bikeName);
                        if (bike) {
                          // effectiveBikePriceCents(bike) is in cents
                          subtotalCents += effectiveBikePriceCents(bike) * quantity * totalDays;
                        }
                      }
                    }
                  );
                }

                // Calculate add-on costs in CENTS (prices are in cents)
                Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
                  const addOn = addOns.find((a) => a.id === addOnId);
                  if (addOn) {
                    // addOn.price is in cents
                    if (addOn.option === "per-booking" && value?.quantity) {
                      subtotalCents += addOn.price * value.quantity;
                    } else if (addOn.option === "per-bike" && value === true) {
                      // For per-bike, multiply by total number of product1 items
                      subtotalCents += addOn.price * totalBikes;
                    } else if (
                      addOn.option === "user-selection" &&
                      value?.quantity
                    ) {
                      subtotalCents += addOn.price * value.quantity;
                    }
                  }
                });

                if (subtotalCents > 0) {
                  // subtotalCents is in CENTS, add platform fee (already in cents)
                  const subtotalWithPlatformFeeCents = subtotalCents + bookingPlatformFee;
                  // Calculate tax in CENTS
                  const taxAmountCents = salesTaxPercent
                    ? Math.round(subtotalWithPlatformFeeCents * (salesTaxPercent / 100))
                    : 0;
                  const finalTotalCents = subtotalWithPlatformFeeCents + taxAmountCents;
                  
                  // Convert to dollars for display
                  const platformFeeDollars = centsToDollars(bookingPlatformFee);
                  const subtotalWithPlatformFee = centsToDollars(subtotalWithPlatformFeeCents);
                  const taxAmount = centsToDollars(taxAmountCents);
                  const finalTotal = centsToDollars(finalTotalCents);

                  return (
                    <>
                      <View style={styles.summaryDivider} />
                      {bookingPlatformFee > 0 && (
                        <View style={styles.summaryItemRow}>
                          <Text style={styles.summaryItemText}>
                            Platform Fee
                          </Text>
                          <Text style={styles.summaryItemPrice}>
                            {formatDollarsAsUSDWithCents(platformFeeDollars)}
                          </Text>
                        </View>
                      )}
                      {salesTaxPercent && taxAmount > 0 && (
                        <View style={styles.summaryItemRow}>
                          <Text style={styles.summaryItemText}>
                            Sales Tax ({salesTaxPercent}%)
                          </Text>
                          <Text style={styles.summaryItemPrice}>
                            {formatDollarsAsUSDWithCents(taxAmount)}
                          </Text>
                        </View>
                      )}
                      <View style={styles.summaryTotalRow}>
                        <Text style={styles.summaryTotalLabel}>
                          Current Total:
                        </Text>
                        <Text style={styles.summaryTotalValue}>
                          {formatDollarsAsUSDWithCents(finalTotal)}
                        </Text>
                      </View>
                    </>
                  );
                }
                return null;
              })()}
            </>
          )}
        </View>
        {/* Customer Information */}
        <View ref={customerInfoRef} style={styles.section}>
          <Text style={styles.sectionTitle}>Customer Information</Text>

          {/* First Name and Last Name - Side by Side */}
          <View style={styles.nameRow}>
            <View style={styles.nameInputContainer}>
              <Text style={styles.label}>
                First Name <Text style={styles.requiredAsterisk}>*</Text>
              </Text>
              <TextInput
                style={[styles.nameInput, isEditMode && styles.inputDisabled]}
                placeholder="First Name"
                placeholderTextColor="#AEB8B6"
                value={firstName}
                onChangeText={(text) => setFirstName(capitalizeAllWords(text))}
                editable={!isEditMode}
              />
            </View>
            <View style={[styles.nameInputContainer, { marginRight: 0 }]}>
              <Text style={styles.label}>
                Last Name <Text style={styles.requiredAsterisk}>*</Text>
              </Text>
              <TextInput
                style={[styles.nameInput, isEditMode && styles.inputDisabled]}
                placeholder="Last Name"
                placeholderTextColor="#AEB8B6"
                value={lastName}
                onChangeText={(text) => setLastName(capitalizeAllWords(text))}
                editable={!isEditMode}
              />
            </View>
          </View>

          <View>
            <Text style={styles.label}>
              Email <Text style={styles.requiredAsterisk}>*</Text>
            </Text>
            <TextInput
              style={[styles.input, isEditMode && styles.inputDisabled]}
              placeholder="Email"
              placeholderTextColor="#AEB8B6"
              value={customerEmail}
              onChangeText={(text) => setCustomerEmail(text.toLowerCase())}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!isEditMode}
            />
          </View>

          <View>
            <Text style={styles.label}>
              Phone Number <Text style={styles.requiredAsterisk}>*</Text>
            </Text>
            <View style={styles.phoneInputContainer}>
              <Text style={styles.phoneFormatText}>(</Text>
              {[0, 1, 2].map((index) => (
                <TextInput
                  key={`phone-${index}`}
                  ref={(el) => (phoneInputRefs.current[index] = el)}
                  style={[
                    styles.phoneDigitInput,
                    isEditMode && styles.inputDisabled,
                  ]}
                  value={phoneDigits[index] || ""}
                  onChangeText={(value) => handlePhoneDigitChange(index, value)}
                  onKeyPress={(e) => handlePhoneDigitKeyPress(index, e)}
                  keyboardType="phone-pad"
                  maxLength={1}
                  editable={!isEditMode}
                  selectTextOnFocus
                />
              ))}
              <Text style={styles.phoneFormatText}>) - </Text>
              {[3, 4, 5].map((index) => (
                <TextInput
                  key={`phone-${index}`}
                  ref={(el) => (phoneInputRefs.current[index] = el)}
                  style={[
                    styles.phoneDigitInput,
                    isEditMode && styles.inputDisabled,
                  ]}
                  value={phoneDigits[index] || ""}
                  onChangeText={(value) => handlePhoneDigitChange(index, value)}
                  onKeyPress={(e) => handlePhoneDigitKeyPress(index, e)}
                  keyboardType="phone-pad"
                  maxLength={1}
                  editable={!isEditMode}
                  selectTextOnFocus
                />
              ))}
              <Text style={styles.phoneFormatText}> - </Text>
              {[6, 7, 8, 9].map((index) => (
                <TextInput
                  key={`phone-${index}`}
                  ref={(el) => (phoneInputRefs.current[index] = el)}
                  style={[
                    styles.phoneDigitInput,
                    isEditMode && styles.inputDisabled,
                  ]}
                  value={phoneDigits[index] || ""}
                  onChangeText={(value) => handlePhoneDigitChange(index, value)}
                  onKeyPress={(e) => handlePhoneDigitKeyPress(index, e)}
                  keyboardType="phone-pad"
                  maxLength={1}
                  editable={!isEditMode}
                  selectTextOnFocus
                />
              ))}
            </View>
          </View>

          {/* Address Input with Google Maps Autocomplete - Only show if Delivery or Home Pickup Needed */}
          {(deliveryNeeded || homePickupNeeded) && (
            <View
              ref={addressSectionRef}
              style={[
                styles.addressInputContainer,
                styles.addressSection,
                styles.addressDropdownWrapper,
                { zIndex: 100001, elevation: 101 },
              ]}
            >
              <Text style={styles.label}>
                Delivery Address <Text style={styles.requiredAsterisk}>*</Text>
              </Text>
              {isEditMode ? (
                <TextInput
                  style={[styles.addressInput, styles.inputDisabled]}
                  value={addressInput}
                  editable={false}
                  placeholder="Delivery Address"
                  placeholderTextColor="#AEB8B6"
                />
              ) : (
                <View style={styles.addressInputRow}>
                  <View style={styles.addressInputWrapper}>
                    <GoogleAddressInput
                      value={addressInput}
                      onChangeText={(text) => {
                        setAddressInput(text);
                        // Clear confirmation when address changes
                        if (addressConfirmed) {
                          setAddressConfirmed(false);
                        }
                      }}
                      onAddressSelected={(selected) => {
                        setAddressInput(selected);
                        // Clear confirmation when address changes
                        if (addressConfirmed) {
                          setAddressConfirmed(false);
                        }
                      }}
                      placeholder="Delivery Address"
                      style={styles.googleAddressInput}
                      isValidated={addressInput.trim().length >= 7 ? (addressConfirmed ? true : false) : undefined}
                    />
                  </View>
                  <Animated.View
                    style={[
                      isValidateEnabled2 && !addressConfirmed && { opacity: validateButtonOpacity2 },
                    ]}
                  >
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.validateButton,
                        !isValidateEnabled2 && styles.validateButtonDisabled,
                        addressConfirmed && styles.validateButtonConfirmed,
                      ]}
                      disabled={!isValidateEnabled2}
                      onPress={() => handleValidateAddress(2)}
                    >
                      <Text style={[
                        styles.validateButtonText,
                        addressConfirmed && styles.validateButtonTextConfirmed,
                      ]}>
                        {addressConfirmed ? "✓ Validated" : "Validate"}
                      </Text>
                    </TouchableOpacity>
                  </Animated.View>
                </View>
              )}
            </View>
          )}
        </View>
        {startDate && endDate && availableBikes.length === 0 && (
          <View style={styles.section}>
            <Text style={styles.infoText}>
              {inventory.length === 0
                ? "Loading inventory..."
                : "No bicycles available for the selected date range"}
            </Text>
          </View>
        )}
        {/* Checkout and Waiver Buttons */}
        {shouldShowCheckoutSection && (
        <View style={styles.checkoutContainer}>
          {(() => {
            // Validation logic for Stripe Checkout button
            const hasFirstName = firstName.trim();
            const hasLastName = lastName.trim();
            const hasEmail = customerEmail.trim();
            const hasPhone = customerPhone.trim();
            const hasDeliveryOption =
              deliveryNeeded || homePickupNeeded || inStorePickup;

            // Calculate final total for checkout validation (must be > $1.00 = 100 cents)
            // Calculate directly from selections, not from rentalCost (which may be null)
            let calculatedSubtotal = 0;
            let totalBikes = 0;

            if (startDate && endDate) {
              const days =
                Math.round(
                  (new Date(endDate) - new Date(startDate)) /
                    (1000 * 60 * 60 * 24)
                ) || 1;

              // Add bicycle costs
              Object.entries(selectedBikes).forEach(([bikeName, quantity]) => {
                if (quantity > 0) {
                  totalBikes += quantity;
                  const bike = inventory.find((b) => b.name === bikeName);
                  if (bike) {
                    calculatedSubtotal += effectiveBikePriceCents(bike) * quantity * days;
                  }
                }
              });

              // Add add-ons costs
              Object.entries(selectedAddOns).forEach(([addOnId, value]) => {
                const addOn = addOns.find((a) => a.id === addOnId);
                if (addOn) {
                  if (addOn.option === "per-booking" && value?.quantity) {
                    calculatedSubtotal += addOn.price * value.quantity;
                  } else if (addOn.option === "per-bike" && value === true) {
                    // For per-bike, multiply by total number of product1 items
                    calculatedSubtotal += addOn.price * totalBikes;
                  } else if (
                    addOn.option === "user-selection" &&
                    value?.quantity
                  ) {
                    calculatedSubtotal += addOn.price * value.quantity;
                  }
                }
              });

              // Add delivery/pickup costs if applicable (rough estimate if rentalCost not available)
              // Note: Exact delivery/pickup cost requires distance calculation, but for validation
              // we just check if total > $1.00, so we can use rentalCost if available for accuracy
              if (rentalCost) {
                // Calculate from CENTS fields to match Display Logic
                const equipmentCostCents = rentalCost.rentalCost?.gross || 0;
                const deliveryCostCents = rentalCost.deliveryApplied || 0;
                const pickupCostCents = rentalCost.pickupApplied || 0;
                const transportationDiscountCents = rentalCost.discountAmount || 0;
                const durationDiscountCents = rentalCost.durationDiscountAmount || 0;
                
                const subtotalCents = equipmentCostCents + deliveryCostCents + pickupCostCents - transportationDiscountCents - durationDiscountCents;
                calculatedSubtotal = subtotalCents / 100; // Convert to Dollars
              } else if ((deliveryNeeded || homePickupNeeded) && hasAddress) {
                // If delivery/pickup needed but rentalCost not calculated yet, we'll rely on
                // bike + add-on costs for now. The exact amount will be calculated during booking.
                // For validation, we just need to know if total > $2.00
              }
            }

            const taxAmount = salesTaxPercent
              ? (calculatedSubtotal * salesTaxPercent) / 100
              : 0;
            const platformFee = bookingPlatformFee / 100;
            const finalTotal = calculatedSubtotal + platformFee + taxAmount;
            const hasMinimumTotal = finalTotal > 2.0; // > $2.00

            // Check if any items (bikes or add-ons) are selected
            const hasBikesSelected = Object.values(selectedBikes).some(
              (qty) => qty > 0
            );
            const hasAddOnsSelected = Object.values(selectedAddOns).some(
              (value) => {
                if (typeof value === "boolean") return value === true;
                if (value && value.quantity) return value.quantity > 0;
                return false;
              }
            );
            const hasItemsSelected = hasBikesSelected || hasAddOnsSelected;

            // Stripe checkout button enabled only when all these requirements are met
            const bookingAllowedCheck = isBookingAllowed();
            const canCheckout =
              hasFirstName &&
              hasLastName &&
              hasEmail &&
              hasPhone &&
              hasDeliveryOption &&
              addressRequirementMet &&
              hasItemsSelected &&
              hasMinimumTotal &&
              bookingAllowedCheck.allowed &&
              !submitting;

            // Manual booking button enabled if items selected and minimum total met
            const canManualBook = hasItemsSelected && hasMinimumTotal && !manualBookingProcessing;

            // In edit mode: only require waiver if bicycles have increased OR add-ons added
            // In new booking mode: always require waiver if bikes OR add-ons selected
            const needsWaiver = isEditMode
              ? haveBicyclesIncreased() || hasAddOnsSelected
              : hasBikesSelected || hasAddOnsSelected;

            // Address validation required if delivery or home pickup is selected
            const addressValidationMet = !needsAddress || addressConfirmed;
            
            const canSignWaiver =
              hasFirstName &&
              hasLastName &&
              hasEmail &&
              hasPhone &&
              hasDeliveryOption &&
              addressRequirementMet &&
              addressValidationMet &&
              needsWaiver &&
              hasMinimumTotal &&
              !submitting &&
              !waiverSigned;

            // Show waiver button only if waiver is needed
            const showWaiverButton = needsWaiver && !waiverSigned;

            // Get list of missing required fields
            const getMissingFields = () => {
              const missing = [];
              if (!hasFirstName)
                missing.push({ text: "First Name", ref: customerInfoRef });
              if (!hasLastName)
                missing.push({ text: "Last Name", ref: customerInfoRef });
              if (!hasEmail)
                missing.push({ text: "Email", ref: customerInfoRef });
              if (!hasPhone)
                missing.push({ text: "Phone Number", ref: customerInfoRef });
              if (!hasDeliveryOption)
                missing.push({
                  text: "Delivery Option (select Delivery and/or Home Pickup, or In-Store Pickup)",
                  ref: deliveryOptionsRef,
                });
              if (needsAddress && !hasAddress)
                missing.push({
                  text: "Delivery Address",
                  ref: addressSectionRef,
                });
              if (!hasItemsSelected)
                missing.push({
                  text: "At least one item (bicycle or add-on)",
                  ref: bicyclesSectionRef,
                });
              if (!hasMinimumTotal)
                missing.push({
                  text: "Minimum order total ($2.00)",
                  ref: null,
                });
              return missing;
            };

            const missingFields = getMissingFields();
            
            // Check booking allowed status (same-day restrictions)
            const bookingAllowedStatus = isBookingAllowed();
            if (!bookingAllowedStatus.allowed) {
              missingFields.unshift({
                text: bookingAllowedStatus.message,
                ref: null,
              });
            }
            
            const hasMissingFields = missingFields.length > 0;

            return (
              <>
                {/* Missing Fields Information Box */}
                {hasMissingFields && (
                  <View style={styles.missingFieldsContainer}>
                    <Text style={styles.missingFieldsTitle}>
                      ⚠️ Required Information Missing
                    </Text>
                    <Text style={styles.missingFieldsText}>
                      Please complete the following before checkout:
                    </Text>
                    {missingFields.map((field, index) =>
                      field.ref ? (
                        <TouchableOpacity
                          key={index}
                          onPress={() => scrollToSection(field.ref)}
                          style={styles.missingFieldItemClickable}
                        >
                          <Text style={styles.missingFieldItemLink}>
                            • {field.text} 👆
                          </Text>
                        </TouchableOpacity>
                      ) : (
                        <Text key={index} style={styles.missingFieldItem}>
                          • {field.text}
                        </Text>
                      )
                    )}
                  </View>
                )}


                {/* Cancellation Policy - shown above waiver button */}
                {showWaiverButton && cancellationPolicy && (
                  <View style={styles.cancellationPolicyContainer}>
                    <Text style={styles.cancellationPolicyTitle}>Cancellation Policy</Text>
                    <Text style={styles.cancellationPolicyText}>
                      {cancellationPolicy}
                    </Text>
                  </View>
                )}

                {/* Sign Waiver Button - only shown if waiver is needed AND user is NOT authenticated */}
                {showWaiverButton && !isAuthenticated && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.checkoutButton,
                      !canSignWaiver && styles.checkoutButtonDisabled,
                    ]}
                    onPress={canSignWaiver ? handleWaiverFlow : undefined}
                    disabled={!canSignWaiver || submitting}
                  >
                    {submitting ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.checkoutButtonText}>
                        {waiverSigned
                          ? "Complete Payment"
                          : isEditMode
                            ? "Sign Updated Waiver"
                            : hasBikesSelected
                              ? "Sign Waiver"
                              : "Sign Equipment Waiver"}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}

                {/* Manual Booking Controls (Authenticated Only) */}
                {isAuthenticated && (
                  <View style={{
                    marginTop: 24,
                    padding: 16,
                    backgroundColor: "#F4F6F6",
                    borderRadius: 6,
                    borderWidth: 1,
                    borderColor: "#DDE3E2",
                  }}>
                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                      <Text style={{ flex: 1, fontSize: 14 }}>Sign waiver & collect payment (Standard Flow)</Text>
                      <Switch
                        value={manualBookingMode === "signPay"}
                        onValueChange={(val) => val && setManualBookingMode("signPay")}
                      />
                    </View>

                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                      <Text style={{ flex: 1, fontSize: 14 }}>Sign Waiver &amp; Pay with Cash</Text>
                      <Switch
                        value={manualBookingMode === "signPayCash"}
                        onValueChange={(val) => val && setManualBookingMode("signPayCash")}
                      />
                    </View>

                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                      <Text style={{ flex: 1, fontSize: 14 }}>Bypass Waiver (Direct to Payment)</Text>
                      <Switch
                        value={manualBookingMode === "bypassWaiver"}
                        onValueChange={(val) => val && setManualBookingMode("bypassWaiver")}
                      />
                    </View>

                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 16 }}>
                      <Text style={{ flex: 1, fontSize: 14 }}>Book Directly (No Payment/Waiver)</Text>
                      <Switch
                        value={manualBookingMode === "direct"}
                        onValueChange={(val) => val && setManualBookingMode("direct")}
                      />
                    </View>

                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.checkoutButton,
                        {
                          // One primary colour for every mode: the selected mode is
                          // already stated by the switches and the button label.
                          backgroundColor: "#0E7C6B",
                          marginTop: 0,
                        },
                        (!canManualBook) && styles.checkoutButtonDisabled,
                      ]}
                      onPress={canManualBook ? handleManualBookingSubmit : undefined}
                      disabled={!canManualBook}
                    >
                      {manualBookingProcessing ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={styles.checkoutButtonText}>
                          {manualBookingMode === "direct"
                            ? "Create Manual Booking"
                            : manualBookingMode === "signPayCash"
                            ? "Sign Waiver & Pay Cash"
                            : manualBookingMode === "signPay"
                            ? (waiverSigned ? "Complete Payment" : "Sign Waiver")
                            : "Start Booking Flow"}
                        </Text>
                      )}
                    </TouchableOpacity>
                  </View>
                )}
              </>
            );
          })()}
        </View>
        )}

        {/* Clear Booking Button */}
        {!isEditMode && (
          <View style={{ padding: 20, alignItems: 'center' }}>
            <TouchableOpacity
              style={{
                backgroundColor: '#C4453D',
                paddingVertical: 12,
                paddingHorizontal: 24,
                borderRadius: 6,
              }}
              onPress={() => {
                if (window.confirm('Are you sure you want to clear all booking data? This cannot be undone.')) {
                  // clearBooking() resets the entire store (including waiverSigned and waiverData)
                  clearBooking();
                  // Clear local UI state
                  setStartDate(null);
                  setEndDate(null);
                  setCurrentMonth(DEFAULT_CURRENT_MONTH);
                  setSelectedBikes({});
                  setSelectedAddOns({});
                  setDeliveryNeeded(DEFAULT_DELIVERY_NEEDED);
                  setHomePickupNeeded(DEFAULT_HOME_PICKUP_NEEDED);
                  setInStorePickup(DEFAULT_IN_STORE_PICKUP);
                  setGatedCommunity(false);
                  setCommunityName("");
                  setAddressNotes("");
                  setFirstName("");
                  setLastName("");
                  setCustomerEmail("");
                  setCustomerPhone("");
                  setAddressInput("");
                  setAddressConfirmed(false);
                  setWaiverSigned(false);
                  setWaiverData(null);
                  setPhoneDigits(["", "", "", "", "", "", "", "", "", ""]);
                }
              }}
              onLongPress={() => {
                // Long-press: populate with default values
                setFirstName(DEFAULT_FIRST_NAME);
                setLastName(DEFAULT_LAST_NAME);
                setCustomerEmail(DEFAULT_CUSTOMER_EMAIL);
                setCustomerPhone(DEFAULT_CUSTOMER_PHONE);
                setAddressInput(DEFAULT_CUSTOMER_ADDRESS);
                // Update phone digits for the 10-box input
                const defaultPhoneDigits = getPhoneDigits(DEFAULT_CUSTOMER_PHONE);
                setPhoneDigits(defaultPhoneDigits);
              }}
            >
              <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: '600' }}>
                Clear Booking
              </Text>
            </TouchableOpacity>
          </View>
        )}
        </View>
      </ScrollView>

      {/* Checking waiver modal (shown while deciding whether to skip waiver page) */}
      <Modal
        visible={showCheckingWaiverModal}
        transparent
        animationType="fade"
        onRequestClose={() => {}}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { padding: 24, alignItems: "center", minWidth: 260 }]}>
            <ActivityIndicator size="large" color="#0E7C6B" style={{ marginBottom: 16 }} />
            <Text style={{ fontSize: 16, color: "#2E3A38", textAlign: "center" }}>
              Checking waiver…
            </Text>
          </View>
        </View>
      </Modal>

      {/* Discount Unlock Modal */}
      <Modal
        visible={discountUnlockModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setDiscountUnlockModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.discountUnlockModalContent}>
            <Text style={styles.discountUnlockEmoji}>
              {unlockedDiscountType === "transportation" ? "🎉" : "⏰"}🎊✨
            </Text>
            <Text style={styles.discountUnlockTitle}>
              🎁 Extra Savings Unlocked! 🎁
            </Text>
            <Text style={styles.discountUnlockMessage}>
              Congratulations! You've unlocked a new {unlockedDiscountType === "transportation" ? "Transportation" : "Duration"} Discount!
            </Text>
            <View style={styles.discountUnlockDetails}>
              <Text style={styles.discountUnlockDiscountName}>
                {unlockedDiscountName}
              </Text>
              <Text style={styles.discountUnlockPercent}>
                {unlockedDiscountPercent}% OFF
              </Text>
            </View>
            <Text style={styles.discountUnlockSubtext}>
              Your order total has been automatically updated with these extra savings! 💰
            </Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.discountUnlockButton}
              onPress={() => setDiscountUnlockModalVisible(false)}
            >
              <Text style={styles.discountUnlockButtonText}>Awesome! 🚀</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Full Screen Image Modal */}
      <Modal
        visible={imageModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setImageModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setImageModalVisible(false)}
        >
          <View style={styles.modalContent}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.closeButton}
              onPress={() => setImageModalVisible(false)}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
            {selectedImageUrl && (
              <img
                src={selectedImageUrl}
                alt="Full screen"
                style={styles.fullScreenImage}
              />
            )}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Blocking Loading Overlay for Manual Booking (all modes) */}
      <Modal
        visible={manualBookingProcessing}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.blockingOverlay}>
          <View style={styles.loadingOverlayContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.loadingOverlayText}>Creating booking...</Text>
            <Text style={styles.loadingOverlaySubtext}>Please wait</Text>
          </View>
        </View>
      </Modal>

      {/* Blocking Loading Overlay for Start Booking Flow (Waiver Flow) */}
      <Modal
        visible={submitting}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.blockingOverlay}>
          <View style={styles.loadingOverlayContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.loadingOverlayText}>Creating booking...</Text>
            <Text style={styles.loadingOverlaySubtext}>Please wait while we process your request</Text>
          </View>
        </View>
      </Modal>

      {/* Address Validation Modal with Google Maps */}
      <Modal
        visible={showAddressValidationModal}
        transparent={true}
        animationType="slide"
        onRequestClose={handleWrongAddress}
      >
        <View style={styles.addressValidationModalOverlay}>
          <View style={styles.addressValidationModalContent}>
            {/* Map Container */}
            <View style={styles.addressValidationMapContainer}>
              {mapLoading ? (
                <View style={styles.addressValidationMapLoading}>
                  <ActivityIndicator size="large" color="#0E7C6B" />
                  <Text style={styles.addressValidationMapLoadingText}>
                    Loading map...
                  </Text>
                </View>
              ) : mapError ? (
                <View style={styles.addressValidationMapError}>
                  <Text style={styles.addressValidationMapErrorText}>
                    {mapError}
                  </Text>
                </View>
              ) : (
                <div
                  ref={mapRef1}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: "400px",
                    backgroundColor: "#DDE3E2",
                    position: "relative",
                  }}
                />
              )}
            </View>

            {/* Bottom Section with Distance and Buttons */}
            <View style={styles.addressValidationBottomSection}>
              {addressDistanceMiles !== null && (
                <Text style={styles.addressValidationDistanceText}>
                  You are {addressDistanceMiles} miles away from the shop
                </Text>
              )}
              <View style={styles.addressValidationButtonRow}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.addressValidationButton, styles.addressValidationButtonConfirm]}
                  onPress={handleConfirmAddress}
                >
                  <Text style={styles.addressValidationButtonText}>Confirm</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.addressValidationButton, styles.addressValidationButtonWrong]}
                  onPress={handleWrongAddress}
                >
                  <Text style={styles.addressValidationButtonText}>Wrong</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Address Wrong Informational Modal */}
      <Modal
        visible={showAddressWrongModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAddressWrongModal(false)}
      >
        <View style={styles.addressWrongModalOverlay}>
          <View style={styles.addressWrongModalContent}>
            <Text style={styles.addressWrongModalTitle}>
              Address Not Found
            </Text>
            <Text style={styles.addressWrongModalMessage}>
              Please try entering the address again. You may want to try a neighboring address or use a more specific location.
            </Text>
            <Text style={styles.addressWrongModalMessage}>
              If you must proceed with a custom address, please enter additional address information in the "Address Notes" field to help our delivery team locate you.
            </Text>
            <Text style={styles.addressWrongModalWarning}>
              ⚠️ Your booking cannot continue until you press "Confirm" in the address validation modal.
            </Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.addressWrongModalButton}
              onPress={() => setShowAddressWrongModal(false)}
            >
              <Text style={styles.addressWrongModalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  content: {
    flex: 1,
    paddingBottom: 220,
  },
  desktopContentWrapper: {
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: 20,
  },
  editModeBanner: {
    backgroundColor: "#B4761B",
    padding: 16,
    borderBottomWidth: 4,
    borderBottomColor: "#B4761B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 4,
  },
  editModeBannerText: {
    fontSize: 15,
    fontWeight: "700",
    color: "#2E3A38",
    textAlign: "center",
    lineHeight: 20,
  },
  adminNotesContainer: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    marginTop: 12,
    marginBottom: 8,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  adminNotesHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 4,
  },
  adminNotesLabel: {
    fontSize: 14,
    fontWeight: "700",
    color: "#0E7C6B",
    flex: 1,
  },
  adminNotesToggle: {
    fontSize: 12,
    fontWeight: "600",
    color: "#0E7C6B",
    paddingLeft: 12,
  },
  adminNotesInput: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    color: "#2E3A38",
    backgroundColor: "#FFFFFF",
    minHeight: 80,
    textAlignVertical: "top",
    marginBottom: 8,
  },
  adminNotesHelper: {
    fontSize: 11,
    color: "#8A9794",
    fontStyle: "italic",
    textAlign: "center",
  },
  inputDisabled: {
    backgroundColor: "#EDF1F0",
    color: "#8A9794",
  },
  header: {
    backgroundColor: "#0E7C6B",
    padding: 20,
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 12,
  },
  navButtons: {
    flexDirection: "row",
    marginTop: 8,
  },
  navButton: {
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
  },
  navButtonText: {
    color: "#0E7C6B",
    fontSize: 16,
    fontWeight: "600",
  },
  section: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    overflow: "visible",
    position: "relative",
    zIndex: 0,
  },
  addressSection: {
    position: "relative",
    zIndex: 9999,
    elevation: 100,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
      marginBottom: 2,
    color: "#2E3A38",
  },
  productSectionTitle: {
    fontSize: 24,
    fontWeight: "800",
    marginBottom: 20,
    color: "#0E7C6B",
    paddingBottom: 12,
    borderBottomWidth: 3,
    borderBottomColor: "#0E7C6B",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  sectionTitleSmall: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 16,
    color: "#2E3A38",
  },
  selectedDatesRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 16,
    paddingVertical: 12,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
  },
  selectedDateContainer: {
    alignItems: "center",
  },
  selectedDateLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
  },
  selectedDateValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#0E7C6B",
  },
  calendarContainer: {
    marginTop: 16,
    marginBottom: 16,
  },
  monthNavigation: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    marginBottom: 8,
  },
  monthButton: {
    padding: 8,
    minWidth: 40,
    alignItems: "center",
  },
  monthButtonText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  monthTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  weekLabels: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  weekLabel: {
    flex: 1,
    textAlign: "center",
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
  },
  calendarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    padding: 4,
  },
  durationDiscountHint: {
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  durationDiscountHintText: {
    fontSize: 14,
    color: "#B4761B",
    fontWeight: "600",
  },
  dateRequiredInfo: {
    marginTop: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  dateRequiredInfoText: {
    fontSize: 16,
    color: "#7A4E12",
    fontWeight: "500",
    lineHeight: 22,
  },
  totalAvailabilityContainer: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#E4F4EC",
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#1F8A5B",
    alignItems: "center",
  },
  totalAvailabilityText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#1F8A5B",
  },
  durationDiscountsToggle: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#0E7C6B",
    alignItems: "center",
  },
  durationDiscountsToggleText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
  },
  durationDiscountsContainer: {
    marginTop: 12,
    marginBottom: 16,
    padding: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  durationDiscountsTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 10,
  },
  dayCell: {
    width: "14.28%",
    aspectRatio: 1,
    alignItems: "center",
    justifyContent: "flex-start",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 4,
    margin: 2,
    backgroundColor: "#FFFFFF",
    paddingTop: 4,
  },
  dayCellDesktop: {
    maxWidth: 50,
    maxHeight: 50,
  },
  todayCell: {
    backgroundColor: "transparent",
    borderColor: "#1F8A5B",
    borderWidth: 1,
  },
  startCell: {
    backgroundColor: "#0E7C6B",
    borderColor: "#0E7C6B",
  },
  endCell: {
    backgroundColor: "#0E7C6B",
    borderColor: "#0E7C6B",
  },
  rangeCell: {
    backgroundColor: "#E3F2EF",
    borderColor: "#0E7C6B",
  },
  pastCell: {
    backgroundColor: "#F6F8F7",
    borderColor: "#DDE3E2",
    opacity: 0.5,
  },
  dayAbbreviationText: {
    fontSize: 10,
    fontWeight: "400",
    color: "#5C6B68",
    textTransform: "lowercase",
    marginBottom: 2,
  },
  dayText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#2E3A38",
  },
  todayText: {
    color: "#1F8A5B",
    fontWeight: "bold",
  },
  selectedText: {
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  rangeText: {
    color: "#0E7C6B",
    fontWeight: "500",
  },
  pastText: {
    color: "#8A9794",
  },
  button: {
    height: 44,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
  },
  primaryButton: {
    backgroundColor: "#0E7C6B",
  },
  buttonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  submitButton: {
    backgroundColor: "#1F8A5B",
    marginTop: 24,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  cancellationPolicyContainer: {
    backgroundColor: "#FDF3E3",
    padding: 16,
    borderRadius: 6,
    marginTop: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  cancellationPolicyTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  cancellationPolicyText: {
    fontSize: 13,
    color: "#2E3A38",
    lineHeight: 18,
  },
  refundInfoSection: {
    backgroundColor: "#FDF3E3",
    padding: 16,
    borderRadius: 6,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  refundInfoTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#B4761B",
    marginBottom: 12,
  },
  refundInfoItem: {
    backgroundColor: "#FFFFFF",
    padding: 10,
    borderRadius: 5,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  refundInfoText: {
    fontSize: 14,
    color: "#2E3A38",
  },
  refundInfoTotal: {
    backgroundColor: "#1F8A5B",
    padding: 12,
    borderRadius: 5,
    marginTop: 8,
    alignItems: "center",
  },
  refundInfoTotalText: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  bikeCard: {
    flexDirection: "column",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  bikeImageContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  bikeImagePlaceholder: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 8,
    backgroundColor: "#EDF1F0",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    alignItems: "center",
    justifyContent: "center",
  },
  bikeImagePlaceholderText: {
    fontSize: 48,
  },
  bikeDetailRow: {
    marginBottom: 8,
    position: "relative",
  },
  bikeNameRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  addOnNameRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  addOnDetails: {
    flex: 1,
  },
  productImage: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 8,
    objectFit: "contain",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    imageRendering: "high-quality",
  },
  productImageDesktop: {
    maxWidth: 300,
    maxHeight: 300,
  },
  bikeName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  includedItemsText: {
    fontSize: 13,
    color: "#5C6B68",
    fontStyle: "italic",
    marginTop: 4,
    marginBottom: 8,
  },
  bikePrice: {
    fontSize: 14,
    color: "#5C6B68",
  },
  bikeAvailability: {
    fontSize: 16,
    color: "#C4453D",
  },
  bikeInfoControlsContainer: {
    backgroundColor: "#F4F6F6",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    flexDirection: "row",
    width: "100%",
  },
  bikePriceAvailabilityRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  bikeQuantityRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  quantitySelector: {
    flexDirection: "row",
    alignItems: "center",
  },
  qtyButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#0E7C6B",
    justifyContent: "center",
    alignItems: "center",
  },
  qtyButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  qtyValue: {
    marginHorizontal: 16,
    fontSize: 16,
    fontWeight: "600",
    minWidth: 30,
    textAlign: "center",
  },
  unavailableText: {
    color: "#8A9794",
    fontSize: 14,
    fontStyle: "italic",
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  loadingContent: {
    alignItems: "center",
    padding: 40,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    minWidth: 300,
  },
  loadingLogo: {
    width: 150,
    height: 100,
    marginBottom: 20,
  },
  loadingIconContainer: {
    position: "relative",
    width: 100,
    height: 100,
    marginBottom: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingIcon: {
    fontSize: 64,
    position: "absolute",
  },
  loadingSpinnerOverlay: {
    position: "absolute",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingTitle: {
    fontSize: 28,
    fontWeight: "700",
    color: "#0E7C6B",
    marginBottom: 8,
    textAlign: "center",
  },
  loadingSubtitle: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 24,
    textAlign: "center",
  },
  loadingBarContainer: {
    width: "100%",
    marginBottom: 16,
  },
  loadingBar: {
    width: "100%",
    height: 4,
    backgroundColor: "#DDE3E2",
    borderRadius: 2,
    overflow: "hidden",
  },
  loadingBarFill: {
    height: "100%",
    backgroundColor: "#0E7C6B",
    borderRadius: 2,
    transition: "width 0.3s ease-out",
  },
  loadingHint: {
    fontSize: 12,
    color: "#8A9794",
    fontStyle: "italic",
    textAlign: "center",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  infoText: {
    fontSize: 14,
    color: "#5C6B68",
    textAlign: "center",
    padding: 16,
  },
  phoneInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  phoneDigitInput: {
    width: 32,
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    textAlign: "center",
    fontSize: 16,
    fontWeight: "500",
    backgroundColor: "#FFFFFF",
    marginHorizontal: 2,
  },
  phoneFormatText: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
    marginHorizontal: 2,
  },
  input: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
    width: "100%",
    minHeight: 44,
    maxHeight: 44,
  },
  nameRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 0,
    width: "100%",
  },
  nameInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 16,
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
    minHeight: 44,
    maxHeight: 44,
    flex: 1,
  },
  costRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  costLabel: {
    fontSize: 14,
    color: "#5C6B68",
  },
  costValue: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
  },
  discountValue: {
    color: "#1F8A5B",
  },
  totalRow: {
    borderTopWidth: 2,
    borderTopColor: "#0E7C6B",
    borderBottomWidth: 0,
    marginTop: 8,
    paddingTop: 12,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  totalValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  calculatingText: {
    fontSize: 12,
    color: "#0E7C6B",
    fontStyle: "italic",
    marginTop: 8,
  },
  addressInputContainer: {
    position: "relative",
    zIndex: 9999,
    marginBottom: 0,
    width: "100%",
    overflow: "visible",
  },
  addressDropdownWrapper: {
    overflow: "visible",
  },
  addressInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
    marginBottom: 0,
    zIndex: 9999,
    width: "100%",
    minHeight: 44,
    maxHeight: 44,
    position: "relative",
    elevation: 20,
  },
  addressInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "100%",
  },
  addressInputWrapper: {
    flex: 1,
    marginTop: 4,
  },
  googleAddressInput: {
    width: "100%",
    zIndex: 9999,
    position: "relative",
  },
  validateButton: {
    marginTop: 4,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#C4453D",
    justifyContent: "center",
    alignItems: "center",
    minWidth: 100,
    height: 44,
  },
  validateButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  validateButtonInner: {
    justifyContent: "center",
    alignItems: "center",
  },
  validateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  validateButtonConfirmed: {
    backgroundColor: "#1F8A5B",
  },
  validateButtonTextConfirmed: {
    color: "#FFFFFF",
  },
  addressConfirmNotice: {
    marginTop: 8,
    fontSize: 13,
    color: "#C4453D",
    fontWeight: "600",
  },
  addressValidationModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  addressValidationModalContent: {
    width: "90%",
    maxWidth: 800,
    height: "80%",
    maxHeight: 600,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  addressValidationMapContainer: {
    flex: 1,
    width: "100%",
    minHeight: 400,
    backgroundColor: "#DDE3E2",
  },
  addressValidationMapLoading: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  addressValidationMapLoadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  addressValidationMapError: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  addressValidationMapErrorText: {
    fontSize: 16,
    color: "#C4453D",
    textAlign: "center",
  },
  addressValidationBottomSection: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
    backgroundColor: "#FFFFFF",
  },
  addressValidationDistanceText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 16,
  },
  addressValidationButtonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  addressValidationButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  addressValidationButtonConfirm: {
    backgroundColor: "#1F8A5B",
  },
  addressValidationButtonWrong: {
    backgroundColor: "#C4453D",
  },
  addressValidationButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  suggestionsContainer: {
    position: "absolute",
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#B4761B",
    borderTopWidth: 0,
    maxHeight: 200,
    boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
    elevation: 20,
    zIndex: 99999,
  },
  suggestionsList: {
    maxHeight: 200,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  suggestionItemLast: {
    borderBottomWidth: 0,
  },
  suggestionMainText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 2,
  },
  suggestionSecondaryText: {
    fontSize: 12,
    color: "#5C6B68",
  },
  loadingMapsText: {
    fontSize: 12,
    color: "#5C6B68",
    fontStyle: "italic",
    marginTop: 4,
  },
  addOnCard: {
    flexDirection: "column",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  addOnPriceControlsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#F4F6F6",
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  addOnPriceText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  addOnName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  addOnPriceRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  infoIcon: {
    padding: 4,
    borderRadius: 8,
    minWidth: 24,
    minHeight: 24,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 7,
  },
  infoIconText: {
    fontSize: 14,
  },
  tooltip: {
    position: "absolute",
    top: 50,
    left: 0,
    right: 0,
    backgroundColor: "#2E3A38",
    padding: 12,
    borderRadius: 6,
    zIndex: 10002,
    elevation: 10002,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
  },
  tooltipOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10001,
    elevation: 10001,
  },
  tooltipText: {
    color: "#FFFFFF",
    fontSize: 14,
    lineHeight: 20,
  },
  tooltipArrow: {
    position: "absolute",
    top: -6,
    left: 20,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: "#2E3A38",
  },
  addOnPrice: {
    fontSize: 14,
    color: "#5C6B68",
  },
  checkbox: {
    width: 28,
    height: 28,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxCheckmark: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
  },
  summaryValue: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
    textAlign: "right",
  },
  summarySectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginTop: 12,
    marginBottom: 8,
  },
  summaryItemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 6,
    paddingLeft: 12,
  },
  summaryItemText: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
  },
  summaryItemPrice: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    textAlign: "right",
  },
  summaryItemSubtotal: {
    fontSize: 12,
    color: "#5C6B68",
    textAlign: "right",
    textDecorationLine: "line-through",
  },
  summaryItemTax: {
    fontSize: 11,
    color: "#5C6B68",
    textAlign: "right",
    fontStyle: "italic",
  },
  summaryDivider: {
    height: 1,
    backgroundColor: "#DDE3E2",
    marginVertical: 12,
  },
  summaryTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 12,
    borderTopWidth: 2,
    borderTopColor: "#0E7C6B",
  },
  summaryTotalLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  summaryTotalValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
  totalNote: {
    fontSize: 12,
    fontWeight: "normal",
    color: "#5C6B68",
    fontStyle: "italic",
  },
  discountText: {
    color: "#1F8A5B",
  },
  discountTierRow: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderLeftWidth: 3,
    borderLeftColor: "#0E7C6B",
    marginVertical: 2,
  },
  discountTierText: {
    fontSize: 13,
    color: "#5C6B68",
  },
  discountTierActive: {
    color: "#1F8A5B",
    fontWeight: "600",
  },
  discountTierInactive: {
    color: "#8A9794",
  },
  discountTierNeeded: {
    color: "#B4761B",
    fontSize: 12,
    fontStyle: "italic",
  },
  discountLabel: {
    marginBottom: 12,
    alignItems: "center",
  },
  discountText: {
    fontSize: 13,
    color: "#1F8A5B",
    fontWeight: "600",
    textAlign: "center",
  },
  discountLink: {
    fontSize: 13,
    color: "#1F8A5B",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  summaryNote: {
    fontSize: 12,
    color: "#5C6B68",
    fontStyle: "italic",
    marginTop: 12,
    textAlign: "center",
  },
  radioButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    marginRight: 12,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  radioButtonSelected: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#0E7C6B",
  },
  radioButtonLabel: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxCheckmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  checkboxLabel: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
  gatedCommunityContainer: {
    marginLeft: 20,
  },
  gatedCommunityCheckbox: {
    borderColor: "#C4453D",
  },
  gatedCommunityChecked: {
    backgroundColor: "#C4453D",
    borderColor: "#C4453D",
  },
  gatedCommunityCheckmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  gatedCommunityLabel: {
    fontSize: 16,
    color: "#C4453D",
    fontWeight: "500",
  },
  infoMessageContainer: {
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 6,
    marginTop: 12,
    marginLeft: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  infoMessageText: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
  },
  infoToggleButton: {
    marginTop: 8,
    paddingVertical: 4,
  },
  infoToggleText: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  addressNotesContainer: {
    marginTop: 12,
    marginLeft: 20,
  },
  addressNotesLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 8,
  },
  addressNotesInput: {
    height: 60,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: "#F4F6F6",
    textAlignVertical: "top",
  },
  communityNameContainer: {
    marginTop: 12,
    marginLeft: 20,
    marginBottom: 12,
  },
  communityNameInput: {
    height: 40,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: "#F4F6F6",
    color: "#2E3A38",
  },
  checkoutContainer: {
    padding: 20,
    paddingBottom: 40,
    backgroundColor: "#FFFFFF",
    borderTopWidth: 1,
    borderTopColor: "#EDF1F0",
    marginTop: 20,
    position: "relative",
    zIndex: 0,
  },
  checkoutButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
  },
  checkoutButtonTop: {
    marginBottom: 12,
  },
  checkoutButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  checkoutButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    color: "#2E3A38",
    marginBottom: 6,
  },
  requiredAsterisk: {
    color: "#C4453D",
    fontSize: 14,
  },
  nameInputContainer: {
    flex: 1,
    marginRight: 8,
    marginBottom: 0,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  blockingOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingOverlayContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 32,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 200,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  loadingOverlayText: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    textAlign: "center",
  },
  loadingOverlaySubtext: {
    marginTop: 8,
    fontSize: 14,
    color: "#5C6B68",
    textAlign: "center",
  },
  modalButton: {
    backgroundColor: "#0E7C6B",
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  modalButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  modalContent: {
    position: "relative",
    width: "90%",
    maxWidth: 800,
    aspectRatio: 1,
  },
  closeButton: {
    position: "absolute",
    top: 20,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "rgba(255, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    elevation: 1000,
  },
  closeButtonText: {
    color: "#FFFFFF",
    fontSize: 32,
    fontWeight: "bold",
  },
  fullScreenImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  missingFieldsContainer: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  missingFieldsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#7A4E12",
    marginBottom: 8,
  },
  missingFieldsText: {
    fontSize: 14,
    color: "#7A4E12",
    marginBottom: 8,
  },
  missingFieldItem: {
    fontSize: 14,
    color: "#7A4E12",
    marginLeft: 8,
    marginTop: 4,
  },
  missingFieldItemClickable: {
    marginTop: 4,
    padding: 4,
    borderRadius: 4,
  },
  missingFieldItemLink: {
    fontSize: 14,
    color: "#0E7C6B",
    marginLeft: 8,
    marginTop: 4,
    textDecorationLine: "underline",
  },
  discountUnlockModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: 32,
    width: "90%",
    maxWidth: 450,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
    borderWidth: 3,
    borderColor: "#B4761B",
  },
  discountUnlockEmoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  discountUnlockTitle: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#C4453D",
    marginBottom: 16,
    textAlign: "center",
  },
  discountUnlockMessage: {
    fontSize: 18,
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 26,
  },
  discountUnlockDetails: {
    backgroundColor: "#E3F2EF",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    width: "100%",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#0E7C6B",
  },
  discountUnlockDiscountName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 8,
  },
  discountUnlockPercent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
  discountUnlockSubtext: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 22,
  },
  discountUnlockButton: {
    backgroundColor: "#1F8A5B",
    paddingHorizontal: 40,
    paddingVertical: 14,
    borderRadius: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 5,
  },
  discountUnlockButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "bold",
  },
  addressWrongModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  addressWrongModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "90%",
    maxWidth: 500,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  addressWrongModalTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
    textAlign: "center",
  },
  addressWrongModalMessage: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 12,
    lineHeight: 24,
    textAlign: "left",
  },
  addressWrongModalWarning: {
    fontSize: 16,
    color: "#C4453D",
    marginTop: 12,
    marginBottom: 20,
    fontWeight: "600",
    lineHeight: 24,
    textAlign: "left",
  },
  addressWrongModalButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  addressWrongModalButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default RentalBookings;
